import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useCall } from '../contexts/CallContext';
import { useGroupCall } from '../contexts/GroupCallContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { VoiceMessagePlayer } from '../components/chat/VoiceMessagePlayer';
import { ChatMessageList } from '../components/chat/ChatMessageList';
import { getExistingSubscription, getPushClientId } from '../utils/pushNotifications';
import { formatTimeOnly, formatDT } from '../utils/timezone';
import { downloadFile, downloadChatAttachment } from '../utils/downloadFile';
import { GroupColorPicker } from '../components/shared/GroupColorPicker';

const CHAT_AVATAR_COLOR_PRESETS = [
  { value: '#2563eb', label: 'Blue' },
  { value: '#16a34a', label: 'Green' },
  { value: '#dc2626', label: 'Red' },
  { value: '#475569', label: 'Grey' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#ea580c', label: 'Orange' },
];

const CHAT_AVATAR_COLOR_ALIASES = {
  blue: '#2563eb',
  green: '#16a34a',
  red: '#dc2626',
  grey: '#475569',
  gray: '#475569',
  yellow: '#eab308',
  orange: '#ea580c',
};

const defaultAvatarColorForType = (type) => (type === 'group' ? '#16a34a' : '#2563eb');
const normalizeAvatarColor = (color, type) => {
  const raw = (color || '').trim().toLowerCase();
  if (CHAT_AVATAR_COLOR_ALIASES[raw]) return CHAT_AVATAR_COLOR_ALIASES[raw];
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
  return defaultAvatarColorForType(type);
};
const getAvatarColorHex = (conversation) => normalizeAvatarColor(conversation?.avatar_color, conversation?.type);
const getAvatarForegroundColor = (hex) => (hex?.toLowerCase() === '#eab308' ? '#111827' : '#ffffff');
const EMPTY_TYPING_USERS = [];
const VOICE_RECORDING_MIME_CANDIDATES = [
  { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
  { mimeType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/mp4;codecs=mp4a.40.2', extension: 'm4a' },
  { mimeType: 'audio/mp4', extension: 'm4a' },
  { mimeType: 'audio/aac', extension: 'aac' },
  { mimeType: '', extension: 'webm' },
];

const stopMediaStreamTracks = (stream) => {
  stream?.getTracks?.().forEach((track) => {
    try {
      track.stop();
    } catch {}
  });
};

const createVoiceMediaRecorder = (stream) => {
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('MediaRecorder is not supported on this browser');
  }

  const canCheckMime = typeof MediaRecorder.isTypeSupported === 'function';
  let lastError = null;

  for (const candidate of VOICE_RECORDING_MIME_CANDIDATES) {
    if (candidate.mimeType && canCheckMime && !MediaRecorder.isTypeSupported(candidate.mimeType)) {
      continue;
    }
    try {
      const recorder = candidate.mimeType
        ? new MediaRecorder(stream, { mimeType: candidate.mimeType })
        : new MediaRecorder(stream);
      const actualType = recorder.mimeType || candidate.mimeType || 'audio/webm';
      const extension = actualType.includes('mp4') ? 'm4a'
        : actualType.includes('aac') ? 'aac'
        : candidate.extension;
      return { recorder, mimeType: actualType, extension };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('No supported voice recording format found');
};

const getUploadSafeAudioMimeType = (mimeType = '') => {
  const baseType = String(mimeType).split(';')[0].trim().toLowerCase();
  if (baseType === 'audio/mp4' || baseType === 'audio/x-m4a') return 'audio/mp4';
  if (baseType === 'audio/aac') return 'audio/aac';
  if (baseType === 'audio/ogg') return 'audio/ogg';
  if (baseType === 'audio/wav' || baseType === 'audio/wave') return 'audio/wav';
  if (baseType === 'audio/mpeg' || baseType === 'audio/mp3') return 'audio/mpeg';
  return 'audio/webm';
};

const getConversationParticipantNames = (conversation) => {
  const details = conversation?.participant_details || {};
  if (Object.keys(details).length > 0) return details;
  return conversation?.participant_names || {};
};

const EMOJI_PICKER_GROUPS = [
  { label: 'Smileys', emojis: ['😀', '😄', '😂', '🤣', '😊', '😍', '😘', '😎', '🤔', '😮', '😢', '😡'] },
  { label: 'Gestures', emojis: ['👍', '👎', '👏', '🙌', '🙏', '🤝', '👌', '👋', '💪', '✌️', '🤙', '🫡'] },
  { label: 'Work', emojis: ['✅', '❌', '⚠️', '🚗', '🚚', '📍', '⏰', '📸', '📎', '📄', '🔧', '🛠️'] },
  { label: 'Objects', emojis: ['❤️', '🔥', '⭐', '💯', '🎉', '🚀', '💡', '📌', '🔔', '📞', '💬', '📦'] },
];
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Search,
  Plus,
  Users,
  UserPlus,
  MoreVertical,
  Pencil,
  Loader2,
  Paperclip,
  Image as ImageIcon,
  FileText,
  Mic,
  MapPin,
  X,
  Download,
  Upload,
  Play,
  Pause,
  Trash2,
  Smile,
  AtSign,
  ArrowLeft,
  ArrowDown,
  Maximize2,
  Check,
  Phone,
  Video,
  History,
  Pin,
  BellOff,
} from 'lucide-react';
import { CallHistoryPanel } from '../components/calls/CallHistoryPanel';

export const Chat = () => {
  const { api, user, onDeviceUpdate, soundEnabled, playNotificationSound, refreshUnreadCount, sendChatWs } = useAuth();
  // Direct Consume from ChatContext so the per-conversation drafts, current
  // conversation, and conv-list search survive navigation away from /chat and
  // F5 refresh.
  const {
    selectedConversationId,
    setSelectedConversationId,
    drafts,
    getDraft,
    setDraft,
    searchQuery,
    setSearchQuery,
  } = useChat();
  const { startCall, call: activeCall } = useCall();
  const { startGroupCall, joinGroupCall, activeConvCalls, groupCall, refreshActiveCall } = useGroupCall();
  const routerLocation = useLocation();
  // 8-tier call privileges — 0 = none, 2 = 1:1, 4/6/8 = group tiers
  const { features: featureFlags } = useFeatureFlags();
  const callLimits = featureFlags?.call_limits || { voice_max: 0, video_max: 0 };

  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [isCallHistoryOpen, setIsCallHistoryOpen] = useState(false);
  const [missedCallCount, setMissedCallCount] = useState(0);

  const fetchMissedCount = useCallback(async () => {
    try {
      const { data } = await api.get('/calls/missed-count');
      setMissedCallCount(data.count || 0);
    } catch (e) { /* badge stays as-is */ }
  }, [api]);

  useEffect(() => {
    fetchMissedCount();
    window.addEventListener('focus', fetchMissedCount);
    return () => window.removeEventListener('focus', fetchMissedCount);
  }, [fetchMissedCount]);

  const openCallHistory = useCallback(() => {
    setIsCallHistoryOpen(true);
    setMissedCallCount(0);
    api.post('/calls/history/seen').catch(() => {});
  }, [api]);

  // selectedConversation is a full object derived from conversations + persisted ID.
  // Local mutable copy supports immediate UI feedback (e.g. when an updated
  // conversation event arrives via WebSocket we replace the whole object), while
  // the ID is mirrored to ChatContext for cross-navigation persistence.
  const [selectedConversation, setSelectedConversationLocal] = useState(null);
  const setSelectedConversation = useCallback((convOrUpdater) => {
    setSelectedConversationLocal((prev) => {
      const next = typeof convOrUpdater === 'function' ? convOrUpdater(prev) : convOrUpdater;
      // Defer the ChatProvider mirror — updaters run during render, and setting
      // another provider's state there triggers React's setState-in-render warning.
      queueMicrotask(() => setSelectedConversationId(next?.id || null));
      return next;
    });
  }, [setSelectedConversationId]);

  // Per-conversation draft: derive from the persisted `drafts` map.
  // `messageInput` value reflects whatever draft belongs to the currently-selected
  // conversation; switching conversations swaps it automatically. Sending a message
  // clears the draft for the current conversation only.
  const messageInput = useMemo(
    () => getDraft(selectedConversationId),
    [selectedConversationId, getDraft]
  );
  const setMessageInput = useCallback((valueOrUpdater) => {
    if (!selectedConversationId) return;
    if (typeof valueOrUpdater === 'function') {
      const currentDraft = drafts[selectedConversationId] || '';
      setDraft(selectedConversationId, valueOrUpdater(currentDraft));
    } else {
      setDraft(selectedConversationId, valueOrUpdater);
    }
  }, [selectedConversationId, drafts, setDraft]);

  const [messages, setMessages] = useState([]);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null);
  const [jumpToBottomState, setJumpToBottomState] = useState({ visible: false, unreadBelow: 0 });
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesScrollAreaRef = useRef(null);
  const messagesContentRef = useRef(null);
  const messageScrollPositionsRef = useRef({});
  const validMessageScrollPositionsRef = useRef(new Set());
  const pendingInitialScrollRef = useRef(null);
  const initialScrollInProgressConversationRef = useRef(null);
  const jumpToBottomRafRef = useRef(null);
  // True while the user is anchored to the bottom of the current conversation.
  // ResizeObserver / new-message handlers will keep snapping to bottom while
  // this is true; user-initiated scroll-up clears it.
  const stickToBottomRef = useRef(true);
  const inputRef = useRef(null);
  // Composer performance: the textarea is uncontrolled. Live text lives in this
  // ref; the context draft is persisted on a 400ms debounce so a keystroke does
  // NOT re-render the whole Chat tree (was causing typing lag on slow devices).
  const liveInputRef = useRef('');
  const draftSaveTimerRef = useRef(null);
  const [hasComposerText, setHasComposerText] = useState(false);
  // Pagination (load-older-on-scroll-up) state for the current conversation
  const [loadingOlder, setLoadingOlder] = useState(false);
  const hasMoreOlderRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const pendingPrependScrollRef = useRef(null);
  const loadOlderMessagesRef = useRef(null);
  // Push-notification deep link (?conversation=<id>) captured at FIRST render —
  // the URL-sync effect below strips the param while conversations are still
  // loading, so reading it lazily from the URL loses the deep link.
  const deepLinkConversationIdRef = useRef(new URLSearchParams(window.location.search).get('conversation'));
  const conversationsRef = useRef([]);
  // Mirrors selectedConversation?.id so async fetches can detect stale responses.
  const selectedConversationIdRef = useRef(null);
  
  // Tenant users for participant selection
  const [tenantUsers, setTenantUsers] = useState([]);

  // Profile photo avatars — blob URLs per user id, fetched once (404 = no photo).
  const [avatarUrls, setAvatarUrls] = useState({});
  const avatarFetchedRef = useRef(new Set());
  const ensureAvatar = useCallback((uid) => {
    if (!uid || avatarFetchedRef.current.has(uid)) return;
    avatarFetchedRef.current.add(uid);
    api.get(`/chat/avatar/${uid}`, { responseType: 'blob' })
      .then((r) => setAvatarUrls((m) => ({ ...m, [uid]: URL.createObjectURL(r.data) })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  const otherParticipantId = useCallback(
    (c) => (c?.type === 'direct' ? (c?.participants || []).find((p) => p !== user?.id) : null),
    [user?.id]
  );
  
  // @Mentions autocomplete
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  
  const [openReactionMenuId, setOpenReactionMenuId] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deletingMessage, setDeletingMessage] = useState(false);

  const isCurrentUserAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Fetch avatars for conversation peers + selected conversation participants.
  useEffect(() => {
    conversations.forEach((c) => { const oid = otherParticipantId(c); if (oid) ensureAvatar(oid); });
  }, [conversations, otherParticipantId, ensureAvatar]);
  useEffect(() => {
    (selectedConversation?.participants || []).forEach(ensureAvatar);
  }, [selectedConversation, ensureAvatar]);

  // Lightbox state — full-screen viewer for images and PDFs
  const [lightboxFile, setLightboxFile] = useState(null); // { url, name, type }

  // Typing indicators — Map<conversation_id, {user_id, user_name, ts}[]>
  const [typingMap, setTypingMap] = useState({});
  // Throttle ref for outgoing typing events (one per 3s while user actively types)
  const typingSendRef = useRef({ lastSentAt: 0, stopTimer: null });
  // Track the highest read_at timestamp per other participant for this conversation
  // so we can show "blue ticks" only after THAT participant has read the message.
  const [readReceipts, setReadReceipts] = useState({}); // { [reader_id]: ISO string }
  
  // New conversation dialog
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [newChatType, setNewChatType] = useState('direct');
  const [newChatName, setNewChatName] = useState('');
  const [newChatAvatarColor, setNewChatAvatarColor] = useState('#2563eb');
  const [newChatAllowLeave, setNewChatAllowLeave] = useState(true);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');

  // Fetch avatars for the participant picker when the New Chat dialog opens.
  useEffect(() => {
    if (isNewChatOpen) tenantUsers.forEach((u) => ensureAvatar(u.id));
  }, [isNewChatOpen, tenantUsers, ensureAvatar]);
  
  // Conversation selection for deletion
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteSummary, setDeleteSummary] = useState(null);
  const [deleteSummaryLoading, setDeleteSummaryLoading] = useState(false);
  
  // Add participants dialog
  const [isAddParticipantsOpen, setIsAddParticipantsOpen] = useState(false);
  const [newParticipantsToAdd, setNewParticipantsToAdd] = useState([]);
  const [addParticipantSearch, setAddParticipantSearch] = useState('');
  
  // Manage participants dialog
  const [isManageParticipantsOpen, setIsManageParticipantsOpen] = useState(false);
  const [confirmRemoveParticipant, setConfirmRemoveParticipant] = useState(null); // {id, name, action: 'Leave'|'Remove'}
  
  // Edit conversation details dialog
  const [isEditConversationOpen, setIsEditConversationOpen] = useState(false);
  const [editConversationName, setEditConversationName] = useState('');
  const [editConversationAvatarColor, setEditConversationAvatarColor] = useState('#2563eb');
  
  // File upload
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [dragFileSummary, setDragFileSummary] = useState({ count: 0, label: 'files' });
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedAudio, setRecordedAudio] = useState(null); // For preview before sending
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingDurationRef = useRef(0);
  
  // Location sharing
  const [stagedLocation, setStagedLocation] = useState(null); // For preview before sending
  
  const getMessagesViewport = () =>
    messagesScrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') || null;

  const isNearMessagesBottom = (threshold = 120) => {
    const viewport = getMessagesViewport();
    if (!viewport) return true;
    return viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < threshold;
  };

  const updateJumpToBottomState = useCallback(() => {
    const viewport = getMessagesViewport();
    if (!viewport || !selectedConversation?.id) {
      setJumpToBottomState({ visible: false, unreadBelow: 0 });
      return;
    }

    const visible = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight > 180;
    if (!visible) {
      setJumpToBottomState(prev => (prev.visible || prev.unreadBelow ? { visible: false, unreadBelow: 0 } : prev));
      return;
    }

    setJumpToBottomState(prev => (
      prev.visible === visible ? prev : { visible, unreadBelow: prev.unreadBelow }
    ));
  }, [selectedConversation?.id]);

  const scheduleJumpToBottomStateUpdate = useCallback(() => {
    if (jumpToBottomRafRef.current) cancelAnimationFrame(jumpToBottomRafRef.current);
    jumpToBottomRafRef.current = requestAnimationFrame(() => {
      jumpToBottomRafRef.current = null;
      updateJumpToBottomState();
    });
  }, [updateJumpToBottomState]);

  const saveCurrentMessageScrollPosition = () => {
    const viewport = getMessagesViewport();
    if (viewport && selectedConversation?.id) {
      const atBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 100;
      messageScrollPositionsRef.current[selectedConversation.id] = {
        scrollTop: viewport.scrollTop,
        atBottom,
      };
      validMessageScrollPositionsRef.current.add(selectedConversation.id);
    }
  };

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    setTimeout(() => {
      const viewport = getMessagesViewport();
      if (viewport) {
        viewport.scrollTo({ top: viewport.scrollHeight, behavior });
        return;
      }
      messagesEndRef.current?.scrollIntoView({ behavior });
    }, 100);
  }, []);

  const handleJumpToBottom = useCallback(() => {
    // User clicked "↓ N new messages" — re-anchor to bottom and let the
    // stick-to-bottom logic keep them there as new content arrives.
    stickToBottomRef.current = true;
    setJumpToBottomState({ visible: false, unreadBelow: 0 });
    scrollToBottom('smooth');
    window.setTimeout(() => {
      const viewport = getMessagesViewport();
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
      setJumpToBottomState({ visible: false, unreadBelow: 0 });
    }, 650);
  }, [scrollToBottom]);

  useEffect(() => {
    setJumpToBottomState({ visible: false, unreadBelow: 0 });
    pendingPrependScrollRef.current = null;
    loadingOlderRef.current = false;
    setLoadingOlder(false);
  }, [selectedConversation?.id]);

  useEffect(() => {
    const viewport = getMessagesViewport();
    if (!viewport || !selectedConversation?.id) return;
    const savePosition = () => {
      if (initialScrollInProgressConversationRef.current === selectedConversation.id) return;
      if (pendingInitialScrollRef.current?.conversationId === selectedConversation.id) return;
      // Near the top → pull the next page of older messages
      if (viewport.scrollTop < 200) loadOlderMessagesRef.current?.();
      const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      const atBottom = distanceFromBottom < 100;
      // Flip the stick-to-bottom flag based on the user's scroll position.
      // <100px = anchored; otherwise = released. New-message & image-load
      // handlers read this flag to decide whether to auto-scroll.
      stickToBottomRef.current = atBottom;
      messageScrollPositionsRef.current[selectedConversation.id] = {
        scrollTop: viewport.scrollTop,
        atBottom,
      };
      validMessageScrollPositionsRef.current.add(selectedConversation.id);
      scheduleJumpToBottomStateUpdate();
    };
    viewport.addEventListener('scroll', savePosition, { passive: true });
    scheduleJumpToBottomStateUpdate();
    return () => {
      savePosition();
      if (jumpToBottomRafRef.current) {
        cancelAnimationFrame(jumpToBottomRafRef.current);
        jumpToBottomRafRef.current = null;
      }
      viewport.removeEventListener('scroll', savePosition);
    };
  }, [selectedConversation?.id, messages.length, scheduleJumpToBottomStateUpdate]);

  // Also scroll when the typing indicator appears so it isn't hidden below the fold
  useEffect(() => {
    const list = selectedConversation ? typingMap[selectedConversation.id] : null;
    if (list && list.length > 0 && stickToBottomRef.current) scrollToBottom('smooth');
  // Scrolls when the typing indicator appears. Keyed on selectedConversation?.id
  // (only the id matters here, not the full object); scrollToBottom is stable ([] deps).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typingMap, selectedConversation?.id]);

  // (Removed: the older capture-phase image/video `load` listener.
  // It's superseded by the ResizeObserver-based stick-to-bottom effect above,
  // which catches *every* layout shift — not just media loads.)

  const resizeComposer = useCallback((textarea) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    const minHeight = 44;
    const maxHeight = Math.max(176, Math.min(360, Math.floor(window.innerHeight * 0.42)));
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${nextHeight}px`;
  }, []);

  // On conversation switch: mirror the persisted draft into the live ref and
  // resize the (remounted) textarea. Any pending debounced save captured the
  // previous conversation's id + value, so it remains safe to fire later.
  useEffect(() => {
    liveInputRef.current = messageInput;
    setHasComposerText(!!messageInput.trim());
    resizeComposer(inputRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId, resizeComposer]);
  
  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Re-sort the conversation list as soon as a call ends (the call-log message
  // bumps last_message) — no reliance on the WS broadcast arriving.
  const prevActiveCallRef = useRef(null);
  useEffect(() => {
    if (prevActiveCallRef.current && !activeCall) fetchConversations();
    prevActiveCallRef.current = activeCall;
  }, [activeCall, fetchConversations]);

  const fetchTenantUsers = useCallback(async () => {
    try {
      const response = await api.get('/chat/users');
      setTenantUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch tenant users:', error);
    }
  }, [api]);
  
  const fetchMessages = useCallback(async (conversationId, options = {}) => {
    try {
      const response = await api.get(`/messages/${conversationId}?limit=50`);
      // Stale-response guard: the user may have switched conversations while this
      // request was in flight (e.g. the People "Chat" deep link racing the
      // previously-open thread). Never apply another conversation's messages.
      if (selectedConversationIdRef.current && selectedConversationIdRef.current !== conversationId) return;
      const fetchedMessages = response.data.messages || [];
      hasMoreOlderRef.current = !!response.data.has_more;
      if (options.positionOnLoad) {
        const saved = validMessageScrollPositionsRef.current.has(conversationId)
          ? messageScrollPositionsRef.current[conversationId]
          : null;
        const firstUnread = fetchedMessages.find(message =>
          message.sender_id &&
          message.sender_id !== user?.id &&
          message.sender_id !== 'system' &&
          message.type !== 'system' &&
          !(message.read_by || []).includes(user?.id)
        );
        // Decide initial scroll behaviour on open:
        //   - If we have a saved position AND the user was reading mid-history
        //     (not at the bottom) → restore the literal scrollTop.
        //   - Otherwise → snap to bottom and stick there (handles first open,
        //     return-from-other-page, and "I was already at the bottom"
        //     cases uniformly).
        // The first-unread auto-jump was intentionally removed — it caused
        // the "always lands at the same higher spot on iPhone" bug.
        // `firstUnreadMessageId` is still set for the in-list "new messages"
        // visual divider rendered by ChatMessageList.
        const restoreScrollTop = saved && !saved.atBottom ? saved.scrollTop : null;
        pendingInitialScrollRef.current = {
          conversationId,
          restoreScrollTop: Number.isFinite(restoreScrollTop) ? restoreScrollTop : null,
        };
        stickToBottomRef.current = restoreScrollTop === null;
        setFirstUnreadMessageId(firstUnread?.id || null);
      }
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    }
  }, [api, user?.id]);

  // Load the previous page of messages when the user scrolls near the top.
  // Prepends older messages and preserves the visual scroll position via
  // pendingPrependScrollRef (consumed by the layout effect below).
  const loadOlderMessages = useCallback(async () => {
    const convId = selectedConversation?.id;
    if (!convId || !hasMoreOlderRef.current || loadingOlderRef.current) return;
    const oldest = messages[0]?.created_at;
    if (!oldest) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const viewport = getMessagesViewport();
    const prevScrollHeight = viewport ? viewport.scrollHeight : 0;
    const prevScrollTop = viewport ? viewport.scrollTop : 0;
    try {
      const response = await api.get(`/messages/${convId}?limit=50&before=${encodeURIComponent(oldest)}`);
      const older = response.data.messages || [];
      hasMoreOlderRef.current = !!response.data.has_more;
      if (older.length > 0) {
        pendingPrependScrollRef.current = { conversationId: convId, prevScrollHeight, prevScrollTop };
        setMessages(prev => (prev[0]?.created_at === oldest ? [...older, ...prev] : prev));
      }
    } catch (error) {
      console.error('Failed to load older messages:', error);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [selectedConversation?.id, messages, api]);

  useEffect(() => {
    loadOlderMessagesRef.current = loadOlderMessages;
  }, [loadOlderMessages]);

  // After older messages are prepended, restore the visual position so the
  // content the user was reading doesn't jump.
  useLayoutEffect(() => {
    const pending = pendingPrependScrollRef.current;
    if (!pending || pending.conversationId !== selectedConversation?.id) return;
    pendingPrependScrollRef.current = null;
    const viewport = getMessagesViewport();
    if (!viewport) return;
    viewport.scrollTop = pending.prevScrollTop + (viewport.scrollHeight - pending.prevScrollHeight);
  }, [messages, selectedConversation?.id]);
  
  // Keep conversationsRef in sync with conversations state
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversation?.id || null;
  }, [selectedConversation?.id]);

  // After conversations load (e.g. after navigating back to /chat), re-resolve
  // the full selectedConversation object from the persisted ID. Runs ONLY while
  // nothing is selected yet — once a conversation object is set (user click or
  // deep link) it must never flip the selection back to the persisted id, which
  // raced the People "Chat" deep link and showed another thread's messages.
  useEffect(() => {
    // A pending push deep link takes priority over the persisted selection.
    if (deepLinkConversationIdRef.current) return;
    if (!selectedConversationId) return;
    if (selectedConversation) return;
    const match = conversations.find((c) => c.id === selectedConversationId);
    if (match) setSelectedConversationLocal(match);
  }, [conversations, selectedConversationId, selectedConversation]);

  useEffect(() => {
    fetchConversations();
    fetchTenantUsers();
  }, [fetchConversations, fetchTenantUsers]);

  // Deep-link: open ?conversation=<id> on load, AND when a push-notification
  // tap forwards a postMessage from the Service Worker.
  useEffect(() => {
    // 1. URL query param on initial mount / navigation. Prefer the ref captured
    //    at first render (survives the URL-sync effect stripping the param
    //    before conversations finish loading — the push deep-link race).
    const convoIdFromUrl = deepLinkConversationIdRef.current
      || new URLSearchParams(window.location.search).get('conversation');
    if (convoIdFromUrl && conversations.length > 0) {
      deepLinkConversationIdRef.current = null;
      const target = conversations.find((c) => c.id === convoIdFromUrl);
      if (target && target.id !== selectedConversation?.id) {
        setSelectedConversation(target);
      }
    }
  }, [conversations, routerLocation.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Service-worker push-click postMessage handler — runs once
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      const msg = event.data || {};
      if (msg.type !== 'PUSH_NOTIFICATION_CLICK') return;
      const data = msg.data || {};
      if (data.type === 'chat_message' && data.conversation_id) {
        // Find the matching conversation in current state and open it
        const target = conversationsRef.current.find((c) => c.id === data.conversation_id);
        if (target) {
          setSelectedConversation(target);
        } else {
          // Conversation not yet loaded — refetch then open
          fetchConversations().then(() => {
            const t2 = conversationsRef.current.find((c) => c.id === data.conversation_id);
            if (t2) setSelectedConversation(t2);
          });
        }
        // Also update URL so subsequent reloads land in the same place
        const url = new URL(window.location.href);
        url.searchParams.set('conversation', data.conversation_id);
        window.history.replaceState({}, '', url.toString());
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [fetchConversations, setSelectedConversation]);

  // Keep URL ?conversation in sync with selectedConversation (so the
  // service-worker focus-suppression knows which conversation we're viewing).
  useEffect(() => {
    // Don't strip a pending deep-link param before conversations have loaded.
    if (!selectedConversation?.id && deepLinkConversationIdRef.current) return;
    const url = new URL(window.location.href);
    if (selectedConversation?.id) {
      url.searchParams.set('conversation', selectedConversation.id);
    } else {
      url.searchParams.delete('conversation');
    }
    window.history.replaceState({}, '', url.toString());
  }, [selectedConversation?.id]);

  // Typing indicator — clean up stale typers every second (auto-expire after 5s)
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      setTypingMap(prev => {
        let changed = false;
        const next = {};
        for (const [convId, list] of Object.entries(prev)) {
          const filtered = list.filter(t => now - t.ts < 5000);
          if (filtered.length !== list.length) changed = true;
          if (filtered.length > 0) next[convId] = filtered;
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Helper: send a throttled typing event (once per 3s while user is typing),
  // and auto-send is_typing=false after 5s of silence.
  const fireTypingEvent = useCallback(() => {
    if (!selectedConversation || !sendChatWs) return;
    const now = Date.now();
    const ref = typingSendRef.current;
    if (now - ref.lastSentAt > 3000) {
      sendChatWs({ type: 'typing', conversation_id: selectedConversation.id, is_typing: true });
      ref.lastSentAt = now;
    }
    if (ref.stopTimer) clearTimeout(ref.stopTimer);
    ref.stopTimer = setTimeout(() => {
      sendChatWs({ type: 'typing', conversation_id: selectedConversation.id, is_typing: false });
      ref.lastSentAt = 0;
    }, 5000);
  }, [selectedConversation, sendChatWs]);

  const stopTypingEvent = useCallback((conversationId = selectedConversation?.id) => {
    const ref = typingSendRef.current;
    if (ref.stopTimer) {
      clearTimeout(ref.stopTimer);
      ref.stopTimer = null;
    }
    ref.lastSentAt = 0;
    if (conversationId && sendChatWs) {
      sendChatWs({ type: 'typing', conversation_id: conversationId, is_typing: false });
    }
  }, [selectedConversation?.id, sendChatWs]);

  const clearTyperFromConversation = useCallback((conversationId, typerId) => {
    if (!conversationId || !typerId) return;
    setTypingMap(prev => {
      const currentList = prev[conversationId] || [];
      const nextList = currentList.filter(t => t.user_id !== typerId);
      if (nextList.length === currentList.length) return prev;
      const next = { ...prev };
      if (nextList.length > 0) next[conversationId] = nextList;
      else delete next[conversationId];
      return next;
    });
  }, []);

  // ── Individual message delete (tombstone) ──
  const applyMessageTombstone = useCallback((messageId, deletedBy) => {
    setMessages(prev => prev.map(m => m.id === messageId ? {
      ...m,
      deleted: true,
      deleted_by: deletedBy,
      content: '',
      file_url: null, file_key: null, file_name: null, file_type: null, file_size: null,
      image_width: null, image_height: null,
      location: null, location_name: null, duration: null,
      reactions: [],
    } : m));
  }, []);

  // Tell the SW we left chat → clear stopTimer when conversation switches
  useEffect(() => {
    const ref = typingSendRef.current;
    return () => {
      if (ref.stopTimer) { clearTimeout(ref.stopTimer); ref.stopTimer = null; }
    };
  }, [selectedConversation?.id]);

  // ── Backend chat presence heartbeat.
  //    The Service Worker always displays pushes. Suppression now happens before
  //    Web Push is sent: while this page is visible AND a conversation is open,
  //    tell the backend which conversation can be suppressed for this user.
  useEffect(() => {
    const HEARTBEAT_MS = 4000;
    let cancelled = false;
    let cachedPushEndpoint = '';
    const pushClientId = getPushClientId();

    getExistingSubscription()
      .then((sub) => {
        if (!cancelled) cachedPushEndpoint = sub?.endpoint || '';
      })
      .catch(() => {});

    const isChatVisible = () =>
      document.visibilityState === 'visible' &&
      document.hasFocus() &&
      window.location.pathname.startsWith('/chat');

    const sendPresence = (visible) => {
      if (!sendChatWs) return;
      sendChatWs({
        type: 'chat_presence',
        conversation_id: visible ? selectedConversation?.id || null : null,
        visible: !!visible && !!selectedConversation?.id,
        push_client_id: pushClientId,
        push_endpoint: cachedPushEndpoint,
        ts: Date.now(),
      });
    };

    const sendHeartbeat = () => {
      if (cancelled) return;
      sendPresence(isChatVisible());
    };

    const sendHidden = () => {
      if (cancelled) return;
      sendPresence(false);
    };

    // Send immediately + on focus/show/visibility changes
    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, HEARTBEAT_MS);
    window.addEventListener('focus', sendHeartbeat);
    window.addEventListener('blur', sendHeartbeat);
    window.addEventListener('pageshow', sendHeartbeat);
    window.addEventListener('pagehide', sendHidden);
    document.addEventListener('visibilitychange', sendHeartbeat);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', sendHeartbeat);
      window.removeEventListener('blur', sendHeartbeat);
      window.removeEventListener('pageshow', sendHeartbeat);
      window.removeEventListener('pagehide', sendHidden);
      document.removeEventListener('visibilitychange', sendHeartbeat);
      sendPresence(false);
    };
  }, [selectedConversation?.id, sendChatWs]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id, { positionOnLoad: true });
      setReadReceipts({});
      if (selectedConversation.type === 'group') refreshActiveCall(selectedConversation.id);
    } else {
      setFirstUnreadMessageId(null);
    }
  }, [selectedConversation, fetchMessages, refreshActiveCall]);

  useLayoutEffect(() => {
    const pending = pendingInitialScrollRef.current;
    if (!pending || pending.conversationId !== selectedConversation?.id) return;

    initialScrollInProgressConversationRef.current = pending.conversationId;

    // Commit the initial scroll position. For "restore mid-history", we set
    // scrollTop literally. For "go to bottom" (the common case), we scroll
    // the sentinel into view AND repeat the scroll over the next ~3 seconds.
    //
    // Why 3 seconds and not just rely on ResizeObserver:
    //   - Slow mobile networks (the user reported iPhone) can take 1-3 s to
    //     load message images / map snapshots.
    //   - iOS Safari can be flaky about ResizeObserver firing for off-screen
    //     elements that grow.
    //   - Aggressive repeat-snap is cheap (no-op when stickToBottomRef is false)
    //     and absolutely bulletproof in the only window where it matters:
    //     the first few seconds after opening a conversation.
    const isBottomMode = !Number.isFinite(pending.restoreScrollTop);

    const snapToBottom = () => {
      if (isBottomMode && !stickToBottomRef.current) return;
      const viewport = getMessagesViewport();
      if (!viewport) return;
      if (isBottomMode) {
        if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
          messagesEndRef.current.scrollIntoView({ block: 'end' });
        } else {
          viewport.scrollTop = viewport.scrollHeight;
        }
      } else {
        viewport.scrollTop = Math.min(pending.restoreScrollTop, viewport.scrollHeight);
      }
    };

    snapToBottom();
    const rafId = requestAnimationFrame(snapToBottom);
    // Aggressive re-snap window — covers image/map/video loads on slow networks
    // up to ~3 seconds after opening. Each timer is a no-op if the user has
    // since scrolled (stickToBottomRef would be false in bottom mode), so this
    // is safe even if the user interacts mid-stream.
    const timeouts = [50, 150, 300, 500, 800, 1200, 1700, 2300, 3000].map((delay) =>
      window.setTimeout(snapToBottom, delay)
    );

    // Mark this conversation as read once we've positioned the view.
    if (selectedConversation?.id && sendChatWs) {
      sendChatWs({ type: 'read', conversation_id: selectedConversation.id });
    }
    if (selectedConversation?.id) {
      api.post(`/conversations/${selectedConversation.id}/read`)
        .then(() => {
          fetchConversations();
          setTimeout(() => refreshUnreadCount(), 200);
        })
        .catch((err) => console.error('Mark read error:', err));
    }

    if (pendingInitialScrollRef.current === pending) {
      pendingInitialScrollRef.current = null;
    }
    const tCleanup = window.setTimeout(() => {
      if (initialScrollInProgressConversationRef.current === pending.conversationId) {
        initialScrollInProgressConversationRef.current = null;
      }
      scheduleJumpToBottomStateUpdate();
    }, 3100);

    return () => {
      cancelAnimationFrame(rafId);
      timeouts.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(tCleanup);
    };
  }, [messages, selectedConversation?.id, api, sendChatWs, fetchConversations, refreshUnreadCount, scheduleJumpToBottomStateUpdate]);

  // Robust "stick to bottom" — observes the actual messages content element
  // for size changes (catches images / videos / map embeds growing after
  // initial layout), AND listens directly for `load` events on media inside
  // the viewport (defense-in-depth on iOS Safari, where ResizeObserver can
  // miss off-screen growth). Snaps to bottom while `stickToBottomRef.current`
  // is true.
  useEffect(() => {
    if (!selectedConversation?.id) return;
    const content = messagesContentRef.current;
    const viewport = getMessagesViewport();
    if (!content || !viewport) return;

    let raf = null;
    const reanchorIfStuck = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        if (!stickToBottomRef.current) return;
        if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
          messagesEndRef.current.scrollIntoView({ block: 'end' });
        } else {
          const v = getMessagesViewport();
          if (v) v.scrollTop = v.scrollHeight;
        }
      });
    };

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(reanchorIfStuck);
      observer.observe(content);
    }

    // Explicit `load` / `loadedmetadata` listener as a Safari-friendly
    // fallback (capture phase because `load` doesn't bubble). Catches images,
    // videos, and map iframes growing AFTER ResizeObserver has settled.
    const onMediaLoad = (event) => {
      const tag = event.target?.tagName;
      if (tag !== 'IMG' && tag !== 'VIDEO' && tag !== 'IFRAME') return;
      reanchorIfStuck();
    };
    viewport.addEventListener('load', onMediaLoad, true);
    viewport.addEventListener('loadedmetadata', onMediaLoad, true);

    return () => {
      if (observer) observer.disconnect();
      viewport.removeEventListener('load', onMediaLoad, true);
      viewport.removeEventListener('loadedmetadata', onMediaLoad, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [selectedConversation?.id, messages.length]);

  const canEditConversationDetails = (conv) =>
    !!conv && (conv.type === 'direct' || (conv.type === 'group' && conv.created_by === user?.id));

  const resolveConversationForCurrentUser = useCallback((conversation) => {
    if (!conversation) return conversation;
    if (conversation.type === 'direct' && conversation.participant_names) {
      const otherId = conversation.participants?.find(p => p !== user?.id);
      if (otherId && conversation.participant_names[otherId]) {
        return { ...conversation, name: conversation.participant_names[otherId] };
      }
    }
    return conversation;
  }, [user?.id]);

  const applyConversationUpdate = useCallback((conversation) => {
    const resolved = resolveConversationForCurrentUser(conversation);
    if (!resolved?.id) return;
    setSelectedConversation(prev => (prev?.id === resolved.id ? { ...prev, ...resolved } : prev));
    setConversations(prev => prev.map(c => (c.id === resolved.id ? { ...c, ...resolved } : c)));
  }, [resolveConversationForCurrentUser, setSelectedConversation]);
  
  // Listen for real-time chat messages via WebSocket
  useEffect(() => {
    const unsubscribe = onDeviceUpdate((deviceId, data) => {
      if (data.type === 'chat_message') {
        const message = data.message;
        if (message?.conversation_id && message?.sender_id) {
          clearTyperFromConversation(message.conversation_id, message.sender_id);
        }
        
        // Safety check: only process if user is a participant in this conversation
        const isParticipant = conversationsRef.current.some(c => c.id === message.conversation_id);
        if (!isParticipant && message.sender_id !== user?.id) {
          // Not a participant — ignore this message entirely
          return;
        }
        
        // Missed-call badge: a call-log message from someone else may be a new
        // missed call for me — refresh the count.
        if (message.type === 'call' && message.sender_id !== user?.id) {
          fetchMissedCount();
        }

        // Server-generated messages ATTRIBUTED to the current user (call logs)
        // have no optimistic copy — append them live with id-dedupe.
        if (message.conversation_id === selectedConversation?.id && message.sender_id === user?.id && message.type === 'call') {
          setMessages(prev => (prev.some(m => m.id === message.id) ? prev : [...prev, message]));
          if (stickToBottomRef.current) scrollToBottom('smooth');
        }

        // If message is for current conversation and not from current user
        if (message.conversation_id === selectedConversation?.id && message.sender_id !== user?.id) {
          const shouldAutoScroll = stickToBottomRef.current;
          setMessages(prev => [...prev, message]);
          if (shouldAutoScroll) {
            setJumpToBottomState({ visible: false, unreadBelow: 0 });
            scrollToBottom('smooth');
          } else {
            setJumpToBottomState(prev => ({ visible: true, unreadBelow: prev.unreadBelow + 1 }));
          }
          // Play sound (no toast since conversation is open) — silent if muted
          const openConvMuted = conversationsRef.current.find(c => c.id === message.conversation_id)?.muted;
          if (!openConvMuted) playNotificationSound();
          // Mark as read immediately since conversation is open, THEN refresh
          // the sidebar + global badge so the just-incremented unread count
          // can't linger on the open conversation (read/refetch race).
          api.post(`/conversations/${selectedConversation.id}/read`)
            .then(() => {
              fetchConversations();
              refreshUnreadCount();
            })
            .catch(() => {});
        }
        
        // If message is for different conversation or no conversation selected
        else if (message.sender_id !== user?.id) {
          // Muted conversations stay silent — no toast, no sound (unread still counts)
          const mutedConv = conversationsRef.current.find(c => c.id === message.conversation_id)?.muted;
          const content = message.type === 'text' ? message.content : 
                        message.type === 'file' ? '📎 File' :
                        message.type === 'voice' ? '🎤 Voice message' :
                        message.type === 'location' ? '📍 Location' : 'Message';
          // Skip in-app toaster on mobile — push handles attention there
          const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
          
          if (!isMobile && !mutedConv) {
            toast(`${message.sender_name}`, {
              description: content,
              duration: 8000,
              className: 'bg-[#2563eb] text-white border-none',
              action: {
                label: 'View',
                onClick: () => {
                  const conv = conversationsRef.current.find(c => c.id === message.conversation_id);
                  if (conv) setSelectedConversation(conv);
                }
              }
            });
          }
          
          if (!mutedConv) playNotificationSound();
          
          // Update global unread count for messages in other conversations
          refreshUnreadCount();
        }
        
        // Always update conversations list
        fetchConversations();
      }
      
      // Handle real-time reaction updates
      if (data.type === 'reaction_update') {
        if (data.conversation_id === selectedConversation?.id) {
          setMessages(prev => prev.map(msg => 
            msg.id === data.message_id 
              ? { ...msg, reactions: data.reactions }
              : msg
          ));
        }
      }

      // Handle a message deleted by its sender (or an admin)
      if (data.type === 'chat_message_deleted') {
        if (data.conversation_id === selectedConversation?.id) {
          applyMessageTombstone(data.message_id, data.deleted_by);
        }
        fetchConversations();
      }

      // Attach an async-fetched link preview card to a message
      if (data.type === 'link_preview_update') {
        if (data.conversation_id === selectedConversation?.id) {
          setMessages(prev => prev.map(msg =>
            msg.id === data.message_id
              ? { ...msg, link_preview: data.link_preview }
              : msg
          ));
        }
      }
      
      // Handle new conversation created by another user
      if (data.type === 'conversation_created') {
        const conv = data.conversation;
        // Skip if current user created it (already added via API response)
        if (conv && conv.participants?.includes(user?.id) && conv.created_by !== user?.id) {
          // Resolve name for direct chats from the receiver's perspective
          if (conv.type === 'direct' && conv.participant_names) {
            const otherId = conv.participants.find(p => p !== user?.id);
            if (otherId && conv.participant_names[otherId]) {
              conv.name = conv.participant_names[otherId];
            }
          }
          setConversations(prev => {
            if (prev.some(c => c.id === conv.id)) return prev;
            return [conv, ...prev];
          });
        }
      }

      if (data.type === 'conversation_updated') {
        applyConversationUpdate(data.conversation);
      }
      
      // Handle conversation deleted by another user
      if (data.type === 'conversation_deleted') {
        const convId = data.conversation_id;
        if (convId) {
          setConversations(prev => prev.filter(c => c.id !== convId));
          if (selectedConversation?.id === convId) {
            setSelectedConversation(null);
            setMessages([]);
          }
        }
      }
      
      // Handle participants added/removed by another user — refresh to get accurate state
      if (data.type === 'participants_changed') {
        const updatedConv = data.conversation;
        if (updatedConv) {
          // Update selected conversation immediately if it's the one that changed
          if (selectedConversation?.id === updatedConv.id) {
            setSelectedConversation(prev => ({ ...prev, ...updatedConv }));
          }
          // Refresh conversations list from server for accurate state
          fetchConversations();
        }
      }
      
      // Handle being removed from a conversation by someone else
      if (data.type === 'participant_removed') {
        const convId = data.conversation_id;
        if (convId) {
          // Remove immediately from local state
          setConversations(prev => prev.filter(c => c.id !== convId));
          if (selectedConversation?.id === convId) {
            setSelectedConversation(null);
            setMessages([]);
            toast.info('You were removed from a group');
          }
        }
      }

      // Typing indicator from another user
      if (data.type === 'typing') {
        const { conversation_id, user_id: typerId, user_name: typerName, is_typing } = data;
        if (!conversation_id || !typerId || typerId === user?.id) return;
        setTypingMap(prev => {
          const currentList = prev[conversation_id] || [];
          const list = currentList.filter(t => t.user_id !== typerId);
          if (is_typing) {
            list.push({ user_id: typerId, user_name: typerName, ts: Date.now() });
          }
          const next = { ...prev };
          if (list.length > 0) next[conversation_id] = list;
          else delete next[conversation_id];
          return next;
        });
      }

      // Read receipt — another participant just opened the conversation
      if (data.type === 'read_receipt') {
        const { conversation_id, reader_id, at } = data;
        if (conversation_id !== selectedConversation?.id) return;
        if (!reader_id || reader_id === user?.id) return;
        setReadReceipts(prev => ({ ...prev, [reader_id]: at }));
        // Also update read_by on local messages so the tick UI flips immediately
        setMessages(prev => prev.map(m => {
          if (m.sender_id !== user?.id) return m;
          if ((m.read_by || []).includes(reader_id)) return m;
          return { ...m, read_by: [...(m.read_by || []), reader_id] };
        }));
      }
    });
    
    return unsubscribe;
  }, [onDeviceUpdate, selectedConversation, fetchConversations, user, playNotificationSound, api, refreshUnreadCount, clearTyperFromConversation, applyConversationUpdate, applyMessageTombstone, scrollToBottom, setSelectedConversation, fetchMissedCount]);
  
  // Clear the composer: cancel any pending debounced draft save (so it can't
  // resurrect the text), wipe ref + persisted draft + DOM value.
  const clearComposer = () => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    liveInputRef.current = '';
    setMessageInput('');
    setHasComposerText(false);
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.style.height = '44px';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const draftText = liveInputRef.current;
    if ((!draftText.trim() && selectedFiles.length === 0 && !recordedAudio && !stagedLocation) || !selectedConversation) return;
    stopTypingEvent(selectedConversation.id);
    
    setSending(true);
    setUploading(true);
    
    let uploadErrors = 0;
    const totalUploadItems = selectedFiles.length + (recordedAudio ? 1 : 0);
    let uploadItemIndex = 0;
    
    try {
      // Send staged location if exists
      if (stagedLocation) {
        await sendStagedLocation();
      }
      
      // Upload recorded audio if exists
      if (recordedAudio) {
        try {
          uploadItemIndex += 1;
          await handleFileUpload(recordedAudio.file, { duration: recordedAudio.duration }, { index: uploadItemIndex, total: totalUploadItems });
          setRecordedAudio(null);
        } catch (err) {
          uploadErrors++;
        }
      }
      
      // Upload files if any
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          try {
            uploadItemIndex += 1;
            await handleFileUpload(file, {}, { index: uploadItemIndex, total: totalUploadItems });
          } catch (err) {
            uploadErrors++;
          }
        }
        setSelectedFiles([]);
      }
      
      // Send text message if any
      if (draftText.trim()) {
        // Extract mentions from message (format: @user_name)
        const mentionRegex = /@(\w+(?:\s\w+)?)/g;
        const mentionMatches = draftText.match(mentionRegex) || [];
        
        // Find user IDs for mentioned names
        const mentionedUserIds = [];
        const participantDetails = getConversationParticipantNames(selectedConversation);
        
        for (const [userId, userName] of Object.entries(participantDetails)) {
          for (const match of mentionMatches) {
            const mentionName = match.slice(1).toLowerCase(); // Remove @ and lowercase
            if (userName.toLowerCase().includes(mentionName)) {
              mentionedUserIds.push(userId);
            }
          }
        }
        
        const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const optimistic = {
          id: tempId,
          conversation_id: selectedConversation.id,
          sender_id: user?.id,
          sender_name: (user?.self_display_name_preference === 'company' && (user?.company || '').trim())
            ? user.company.trim()
            : (user?.full_name || user?.username || 'You'),
          content: draftText.trim(),
          type: 'text',
          mentions: mentionedUserIds,
          created_at: new Date().toISOString(),
          read_by: [user?.id],
          delivered_to: [user?.id],
          _pending: true,
        };
        // Immediately show the bubble — feels instant on slow networks
        setMessages(prev => [...prev, optimistic]);
        scrollToBottom('smooth');
        const submittedInput = draftText.trim();
        clearComposer();
        setShowMentions(false);
        try {
          const response = await api.post('/messages', {
            conversation_id: selectedConversation.id,
            content: submittedInput,
            type: 'text',
            mentions: mentionedUserIds,
          });
          // Replace optimistic message with the canonical one from the server
          setMessages(prev => prev.map(m => (m.id === tempId ? response.data : m)));
        } catch (err) {
          // Mark optimistic message as failed so the user can retry / see the error
          setMessages(prev => prev.map(m => (m.id === tempId ? { ...m, _pending: false, _failed: true } : m)));
          throw err;
        }
      }
      
      fetchConversations();
      
      // Show success if files uploaded
      if ((selectedFiles.length > 0 || recordedAudio) && uploadErrors === 0) {
        toast.success('Files uploaded successfully');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      setUploading(false);
      setUploadProgress(null);
      // Refocus input so user can type the next message immediately
      // Use 100ms delay to ensure React has re-rendered (disabled=false) before focusing
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Handle @mention input
  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    liveInputRef.current = value;
    resizeComposer(e.target);
    const hasText = !!value.trim();
    setHasComposerText((prev) => (prev === hasText ? prev : hasText));

    // Debounced draft persist — captures the conversation id AND value at
    // keystroke time so a late fire (after switching conversations) still
    // writes the right draft to the right conversation.
    const convId = selectedConversationId;
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      draftSaveTimerRef.current = null;
      setDraft(convId, value);
    }, 400);

    // Fire a throttled typing indicator to other participants
    if (hasText) fireTypingEvent();
    
    // Check for @ symbol to trigger mentions
    const textBeforeCursor = value.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      // Only show mentions if there's no space in the query or it just started
      if (!textAfterAt.includes(' ') || textAfterAt.split(' ').length <= 2) {
        setShowMentions(true);
        setMentionQuery(textAfterAt.toLowerCase());
        setMentionStartIndex(lastAtIndex);
        setSelectedMentionIndex(0);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleInsertEmoji = (emoji) => {
    const textarea = inputRef.current;
    const current = liveInputRef.current;
    const start = textarea?.selectionStart ?? current.length;
    const end = textarea?.selectionEnd ?? current.length;
    const nextValue = `${current.slice(0, start)}${emoji}${current.slice(end)}`;
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    liveInputRef.current = nextValue;
    setMessageInput(nextValue);
    setHasComposerText(true);
    if (textarea) {
      textarea.value = nextValue;
      resizeComposer(textarea);
    }
    setShowMentions(false);
    fireTypingEvent();
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const nextCursor = start + emoji.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  // Filter participants for mention suggestions
  const mentionSuggestions = useMemo(() => {
    const details = getConversationParticipantNames(selectedConversation);
    if (Object.keys(details).length === 0) return [];
    
    return Object.entries(details)
      .filter(([userId, userName]) => 
        userId !== user?.id && 
        userName.toLowerCase().includes(mentionQuery)
      )
      .slice(0, 5);
  }, [selectedConversation, mentionQuery, user?.id]);

  // Handle selecting a mention from autocomplete
  const handleSelectMention = (userId, userName) => {
    // Get text before the @ symbol
    const currentInput = liveInputRef.current;
    const beforeMention = currentInput.slice(0, mentionStartIndex);
    
    // Find the end of the current mention query (either space or end of string)
    const textAfterAt = currentInput.slice(mentionStartIndex + 1);
    const spaceIndex = textAfterAt.indexOf(' ');
    const afterMention = spaceIndex !== -1 ? textAfterAt.slice(spaceIndex) : '';
    
    // Use first name only
    const mentionText = userName.split(' ')[0];
    const newValue = `${beforeMention}@${mentionText}${afterMention ? afterMention : ' '}`;
    
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    liveInputRef.current = newValue;
    setMessageInput(newValue);
    setHasComposerText(!!newValue.trim());
    if (inputRef.current) {
      inputRef.current.value = newValue;
      resizeComposer(inputRef.current);
    }
    setShowMentions(false);
    
    // Focus input and move cursor to end
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newValue.length, newValue.length);
      }
    }, 0);
  };

  // Handle keyboard navigation in mentions dropdown and composer submit/newline
  const handleMentionKeyDown = (e) => {
    if (showMentions) {
      const suggestions = mentionSuggestions;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        return;
      } else if (e.key === 'Enter' && suggestions.length > 0) {
        e.preventDefault();
        const [userId, userName] = suggestions[selectedMentionIndex];
        handleSelectMention(userId, userName);
        return;
      } else if (e.key === 'Escape') {
        setShowMentions(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  // Handle reaction toggle
  const handleToggleReaction = useCallback(async (messageId, emoji) => {
    try {
      const response = await api.post(`/messages/${messageId}/reactions`, { emoji });
      
      // Update local state immediately
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, reactions: response.data.reactions }
          : msg
      ));
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
      toast.error('Failed to add reaction');
    }
  }, [api]);

  const handleDownloadAttachment = useCallback((message) => {
    if (!message) return;
    downloadChatAttachment(api, message.id, message.file_url, message.file_name);
  }, [api]);

  const handleLightboxDownload = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!lightboxFile) return;
    if (lightboxFile.id) {
      downloadChatAttachment(api, lightboxFile.id, lightboxFile.url, lightboxFile.name);
    } else {
      downloadFile(lightboxFile.url, lightboxFile.name);
    }
  }, [api, lightboxFile]);

  const handleDeleteMessage = async () => {
    if (!messageToDelete) return;
    setDeletingMessage(true);
    try {
      await api.delete(`/messages/${messageToDelete.id}`);
      applyMessageTombstone(messageToDelete.id, user?.id);
      fetchConversations();
      toast.success('Message deleted');
      setMessageToDelete(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    } finally {
      setDeletingMessage(false);
    }
  };

  const getAdminDeleteToast = (baseText, deletePayloads = []) => {
    if (!isCurrentUserAdmin) return baseText;
    const totals = deletePayloads.reduce((acc, payload) => {
      acc.filesDeleted += Number(payload?.files_deleted || 0);
      acc.fileErrors += Array.isArray(payload?.file_delete_errors) ? payload.file_delete_errors.length : 0;
      return acc;
    }, { filesDeleted: 0, fileErrors: 0 });
    const fileLabel = `${totals.filesDeleted} file${totals.filesDeleted === 1 ? '' : 's'} removed`;
    return totals.fileErrors > 0
      ? `${baseText} · ${fileLabel} · ${totals.fileErrors} cleanup issue${totals.fileErrors === 1 ? '' : 's'}`
      : `${baseText} · ${fileLabel}`;
  };

  const fetchDeleteSummary = async (conversationIds) => {
    if (!conversationIds?.length) {
      setDeleteSummary(null);
      return;
    }
    setDeleteSummaryLoading(true);
    try {
      const response = await api.post('/conversations/delete-summary', { conversation_ids: conversationIds });
      setDeleteSummary(response.data);
    } catch (error) {
      console.error('Delete summary error:', error);
      setDeleteSummary(null);
      toast.error(error.response?.data?.detail || 'Could not load delete summary');
    } finally {
      setDeleteSummaryLoading(false);
    }
  };

  const handleCreateConversation = async () => {
    if (selectedParticipants.length === 0) {
      toast.error('Please select a participant');
      return;
    }
    
    if (newChatType === 'direct' && selectedParticipants.length !== 1) {
      toast.error('Direct chat requires exactly 1 participant');
      return;
    }
    
    if (newChatType === 'group' && !newChatName.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    
    if (newChatType === 'group' && selectedParticipants.length < 1) {
      toast.error('Please select at least one participant for the group');
      return;
    }
    
    // For direct chats, check if conversation already exists with this participant
    if (newChatType === 'direct') {
      const participantId = selectedParticipants[0];
      const existingChat = conversations.find(conv => 
        conv.type === 'direct' && 
        conv.participants.includes(participantId) &&
        conv.participants.includes(user?.id)
      );
      
      if (existingChat) {
        // Open existing chat instead of creating new one
        setSelectedConversation(existingChat);
        setIsNewChatOpen(false);
        setNewChatName('');
        setSelectedParticipants([]);
        setParticipantSearch('');
        toast.info('Opened existing conversation');
        return;
      }
    }
    
    try {
      const response = await api.post('/conversations', {
        type: newChatType,
        name: newChatType === 'group' ? newChatName : null,
        avatar_color: newChatAvatarColor,
        participants: selectedParticipants,
        allow_leave: newChatType === 'group' ? newChatAllowLeave : true
      });
      
      setConversations(prev => {
        if (prev.some(c => c.id === response.data.id)) return prev;
        return [response.data, ...prev];
      });
      setSelectedConversation(response.data);
      setIsNewChatOpen(false);
      setNewChatName('');
      setNewChatAvatarColor(defaultAvatarColorForType(newChatType));
      setNewChatAllowLeave(true);
      setSelectedParticipants([]);
      setParticipantSearch('');
      toast.success('Conversation created');
      refreshUnreadCount();
    } catch (error) {
      console.error('Failed to create conversation:', error);
      toast.error('Failed to create conversation');
    }
  };
  
  const toggleParticipant = (userId) => {
    if (newChatType === 'direct') {
      // For direct chat, only allow 1 participant (toggle selection)
      setSelectedParticipants(prev => 
        prev.includes(userId) ? [] : [userId]
      );
    } else {
      // For group chat, allow multiple participants
      setSelectedParticipants(prev => 
        prev.includes(userId) 
          ? prev.filter(id => id !== userId)
          : [...prev, userId]
      );
    }
  };

  // "Select all / Deselect all" toggle in the group-chat participant picker. Operates on
  // the CURRENTLY-VISIBLE set (i.e. after the search filter) so a user can search "manager"
  // then click Select All to add every visible manager without picking them one-by-one.
  const toggleSelectAllVisible = (visibleUserIds) => {
    if (!visibleUserIds || visibleUserIds.length === 0) return;
    setSelectedParticipants(prev => {
      const prevSet = new Set(prev);
      const allVisibleSelected = visibleUserIds.every(id => prevSet.has(id));
      if (allVisibleSelected) {
        // Deselect every currently-visible participant, keep off-list selections.
        const removed = new Set(visibleUserIds);
        return prev.filter(id => !removed.has(id));
      }
      // Add every visible id that isn't already selected.
      const merged = [...prev];
      visibleUserIds.forEach(id => { if (!prevSet.has(id)) merged.push(id); });
      return merged;
    });
  };
  
  // Reset participants when chat type changes
  useEffect(() => {
    setSelectedParticipants([]);
    setNewChatAvatarColor(defaultAvatarColorForType(newChatType));
  }, [newChatType]);
  
  // Check if user is the creator of a conversation
  const isConversationCreator = (conv) => conv?.created_by === user?.id;

  const openEditConversationDialog = () => {
    if (!selectedConversation) return;
    setEditConversationName(selectedConversation.type === 'group' ? (selectedConversation.name || '') : '');
    setEditConversationAvatarColor(getAvatarColorHex(selectedConversation));
    setIsEditConversationOpen(true);
  };

  const handleSaveConversationDetails = async () => {
    if (!selectedConversation) return;
    const payload = { avatar_color: editConversationAvatarColor };
    if (selectedConversation.type === 'group') {
      const name = editConversationName.trim();
      if (!name) {
        toast.error('Group name is required');
        return;
      }
      payload.name = name;
    }
    try {
      const response = await api.patch(`/conversations/${selectedConversation.id}/settings`, payload);
      applyConversationUpdate(response.data);
      setIsEditConversationOpen(false);
      toast.success(selectedConversation.type === 'group' ? 'Group chat updated' : 'Avatar colour updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update chat details');
    }
  };
  
  // Delete conversation handler
  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation(); // Prevent selecting the conversation
    
    try {
      const deleteResponse = await api.delete(`/conversations/${conversationId}`);
      
      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      // Clear selection if deleted conversation was selected
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      
      toast.success(<span data-testid="chat-delete-toast-message">{getAdminDeleteToast('Conversation deleted', [deleteResponse.data])}</span>);
      refreshUnreadCount();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };
  
  // Delete multiple selected conversations
  const handleDeleteSelectedConversations = async () => {
    if (selectedConversations.length === 0) return;
    
    try {
      // Delete all selected conversations
      const deleteResponses = await Promise.all(
        selectedConversations.map(id => api.delete(`/conversations/${id}`))
      );
      
      // Remove from local state
      setConversations(prev => prev.filter(c => !selectedConversations.includes(c.id)));
      
      // Clear selection if any deleted conversation was selected
      if (selectedConversations.includes(selectedConversation?.id)) {
        setSelectedConversation(null);
        setMessages([]);
      }
      
      toast.success(
        <span data-testid="chat-delete-toast-message">
          {getAdminDeleteToast(
            `${selectedConversations.length} conversation${selectedConversations.length > 1 ? 's' : ''} deleted`,
            deleteResponses.map(response => response.data)
          )}
        </span>
      );
      setSelectedConversations([]);
      setIsSelectionMode(false);
      setDeleteSummary(null);
      setIsDeleteDialogOpen(false);
      refreshUnreadCount();
    } catch (error) {
      console.error('Failed to delete conversations:', error);
      toast.error('Failed to delete some conversations');
    }
  };
  
  // Toggle conversation selection
  const toggleConversationSelection = (conversationId, e) => {
    e.stopPropagation();
    setSelectedConversations(prev => 
      prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId]
    );
  };
  
  // Add participants to group
  const handleAddParticipants = async () => {
    if (newParticipantsToAdd.length === 0 || !selectedConversation) return;
    
    try {
      const response = await api.post(`/conversations/${selectedConversation.id}/participants`, {
        participant_ids: newParticipantsToAdd
      });
      
      // Update local conversation data
      setSelectedConversation(response.data);
      setConversations(prev => 
        prev.map(c => c.id === response.data.id ? response.data : c)
      );
      
      // Refresh messages to show system message
      fetchMessages(selectedConversation.id);
      
      setIsAddParticipantsOpen(false);
      setNewParticipantsToAdd([]);
      setAddParticipantSearch('');
      toast.success('Participants added successfully');
    } catch (error) {
      console.error('Failed to add participants:', error);
      toast.error(error.response?.data?.detail || 'Failed to add participants');
    }
  };
  
  // Get users not in the current conversation (for adding)
  const availableParticipants = tenantUsers.filter(
    u => !selectedConversation?.participants?.includes(u.id)
  );

  // Group chat participants by their group label (Account Users / Drivers / Staff / Passengers)
  const PARTICIPANT_GROUP_ORDER = ['Account Users', 'Drivers', 'Staff', 'Passengers'];
  const buildParticipantGroups = (list, search) => {
    const term = (search || '').toLowerCase();
    const filtered = (list || []).filter(u => {
      if (!term) return true;
      const name = (u.full_name || u.email || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
    const byGroup = {};
    filtered.forEach(u => {
      const g = u.group || 'Account Users';
      if (!byGroup[g]) byGroup[g] = [];
      byGroup[g].push(u);
    });
    const orderedKeys = [
      ...PARTICIPANT_GROUP_ORDER.filter(g => byGroup[g]),
      ...Object.keys(byGroup).filter(g => !PARTICIPANT_GROUP_ORDER.includes(g)),
    ];
    return orderedKeys.map(g => ({
      group: g,
      users: byGroup[g].sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '')),
    }));
  };
  
  // Remove participant from group
  const handleRemoveParticipant = async (participantId) => {
    if (!selectedConversation) return;
    
    try {
      const response = await api.delete(`/conversations/${selectedConversation.id}/participants`, {
        data: { participant_id: participantId }
      });
      
      // Check if current user was removed (left the group)
      if (participantId === user?.id) {
        setSelectedConversation(null);
        setMessages([]);
        fetchConversations();
        toast.success('You left the group');
        return;
      }
      
      // Update local conversation data
      setSelectedConversation(response.data);
      setConversations(prev => 
        prev.map(c => c.id === response.data.id ? response.data : c)
      );
      
      // Refresh messages to show system message
      fetchMessages(selectedConversation.id);
      
      toast.success('Participant removed');
    } catch (error) {
      console.error('Failed to remove participant:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove participant');
    }
  };
  
  // Get participant names for the current conversation
  const getParticipantInfo = (participantId) => {
    const userInfo = tenantUsers.find(u => u.id === participantId);
    if (userInfo) {
      return { name: userInfo.full_name || userInfo.email, role: userInfo.role };
    }
    // Check in conversation participant_names
    const name = selectedConversation?.participant_names?.[participantId];
    return { name: name || 'Unknown', role: '' };
  };
  
  // Export messages handler
  const handleExportMessages = async (format = 'csv') => {
    if (!selectedConversation) return;
    
    try {
      // Get user's timezone from browser (or could fetch from user settings)
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await api.get(`/messages/${selectedConversation.id}/export?format=${format}&timezone=${encodeURIComponent(userTimezone)}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedConversation.name || 'chat'}_messages.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Messages exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export messages');
    }
  };
  
  const addFilesToSelection = (fileList, source = 'picker') => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    // Keep these in sync with backend allowlist (server.py)
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif',
      'application/pdf',
      'audio/webm', 'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/aac', 'audio/ogg', 'audio/mp4', 'audio/x-m4a',
      'video/mp4', 'video/webm', 'video/quicktime', 'video/x-quicktime',
    ];

    const validFiles = [];
    for (const file of files) {
      // Validate file size — videos get 100MB cap, others 50MB
      const isVideo = file.type.startsWith('video/');
      const maxSizeMb = isVideo ? 100 : 50;
      if (file.size > maxSizeMb * 1024 * 1024) {
        toast.error(`${file.name} exceeds ${maxSizeMb}MB limit`);
        continue;
      }

      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} type not allowed`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      if (source === 'drop') {
        toast.success(`${validFiles.length} file${validFiles.length > 1 ? 's' : ''} ready to send`);
      }
    }
  };

  const handleFileSelect = (e) => {
    addFilesToSelection(e.target.files, 'picker');
    e.target.value = ''; // Reset input to allow selecting same file again
  };

  const dataTransferHasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

  const summarizeDraggedFiles = (event) => {
    const items = Array.from(event.dataTransfer?.items || []).filter(item => item.kind === 'file');
    const files = items.length > 0 ? items : Array.from(event.dataTransfer?.files || []);
    const count = files.length;
    const categories = new Set();

    files.forEach((item) => {
      const type = item.type || '';
      if (type.startsWith('image/')) categories.add('images');
      else if (type === 'application/pdf') categories.add('PDFs');
      else if (type.startsWith('video/')) categories.add('videos');
      else if (type.startsWith('audio/')) categories.add('audio');
      else categories.add('files');
    });

    const label = categories.size > 0 ? Array.from(categories).join(', ') : 'files';
    return { count, label };
  };

  const handleChatDragEnter = (event) => {
    if (!selectedConversation || uploading || isRecording || !dataTransferHasFiles(event)) return;
    event.preventDefault();
    dragCounterRef.current += 1;
    setDragFileSummary(summarizeDraggedFiles(event));
    setIsDraggingFiles(true);
  };

  const handleChatDragOver = (event) => {
    if (!selectedConversation || uploading || isRecording || !dataTransferHasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragFileSummary(summarizeDraggedFiles(event));
  };

  const handleChatDragLeave = (event) => {
    if (!dataTransferHasFiles(event)) return;
    event.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDraggingFiles(false);
      setDragFileSummary({ count: 0, label: 'files' });
    }
  };

  const handleChatDrop = (event) => {
    if (!selectedConversation || uploading || isRecording || !dataTransferHasFiles(event)) return;
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingFiles(false);
    setDragFileSummary({ count: 0, label: 'files' });
    addFilesToSelection(event.dataTransfer.files, 'drop');
  };
  
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const uploadFileWithProgress = (uploadUrl, file, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('S3 upload failed'));
    xhr.send(file);
  });

  // Read intrinsic image dimensions client-side (zero server cost) so bubbles
  // can reserve the exact aspect ratio before the image loads.
  const getImageDimensions = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

  const handleFileUpload = async (file, extra = {}, progressMeta = {}) => {
    if (!selectedConversation) return;
    
    try {
      // Step 1: Get presigned URL
      const presignedResponse = await api.post('/chat/presigned-url', {
        filename: file.name,
        file_type: file.type,
        file_size: file.size,
        conversation_id: selectedConversation.id
      });
      
      const { upload_url, download_url } = presignedResponse.data;
      const fileKey = presignedResponse.data.file_key;
      
      // Step 2: Upload to S3 with progress for large files/videos
      const isVideo = file.type.startsWith('video/');
      setUploadProgress({
        fileName: file.name,
        percent: 0,
        index: progressMeta.index || 1,
        total: progressMeta.total || 1,
        isVideo,
      });
      await uploadFileWithProgress(upload_url, file, (percent) => {
        setUploadProgress({
          fileName: file.name,
          percent,
          index: progressMeta.index || 1,
          total: progressMeta.total || 1,
          isVideo,
        });
      });
      
      // Step 3: Send message with file metadata
      const messageType = file.type.startsWith('image/') ? 'file' : 
                          file.type.startsWith('audio/') ? 'voice' : 'file';
      
      const payload = {
        conversation_id: selectedConversation.id,
        content: file.name,
        type: messageType,
        file_url: download_url,
        file_key: fileKey,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
      };
      if (file.type.startsWith('image/')) {
        const dims = await getImageDimensions(file);
        if (dims?.width > 0 && dims?.height > 0) {
          payload.image_width = dims.width;
          payload.image_height = dims.height;
        }
      }
      // Capture duration (seconds) for voice messages so we can show "0:15 / 0:42" on iOS
      // (iOS Safari can't read duration from WebM blobs — see VoiceMessagePlayer below)
      if (extra.duration != null) {
        payload.duration = extra.duration;
      }
      const response = await api.post('/messages', payload);
      
      setMessages(prev => [...prev, response.data]);
      scrollToBottom('smooth');
      fetchConversations();
      
      // Success!
      console.log('File uploaded successfully:', file.name);
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(`Failed to upload ${file.name}`);
      throw error; // Re-throw so calling function knows it failed
    }
  };
  
  const startRecording = async () => {
    if (isRecording) return;
    let stream = null;
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone recording is not supported on this browser');
      }

      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const { recorder: mediaRecorder, mimeType, extension } = createVoiceMediaRecorder(stream);
      
      audioChunksRef.current = [];
      recordingDurationRef.current = 0;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const uploadMimeType = getUploadSafeAudioMimeType(mimeType);
        const uploadExtension = uploadMimeType === 'audio/mp4' ? 'm4a'
          : uploadMimeType === 'audio/aac' ? 'aac'
          : uploadMimeType === 'audio/ogg' ? 'ogg'
          : uploadMimeType === 'audio/wav' ? 'wav'
          : uploadMimeType === 'audio/mpeg' ? 'mp3'
          : extension;
        const audioBlob = new Blob(audioChunksRef.current, { type: uploadMimeType });
        const audioFile = new File([audioBlob], `voice-${Date.now()}.${uploadExtension}`, { type: uploadMimeType });
        
        stopMediaStreamTracks(stream);
        
        // Add to preview instead of auto-uploading
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio({ file: audioFile, url: audioUrl, duration: recordingDurationRef.current });
        setRecordingTime(0);
        recordingDurationRef.current = 0;
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      
      // Timer
      let seconds = 0;
      recordingTimerRef.current = setInterval(() => {
        seconds++;
        recordingDurationRef.current = seconds;
        setRecordingTime(seconds);
        if (seconds >= 60) {
          stopRecording();
        }
      }, 1000);
      
    } catch (error) {
      console.error('Recording error:', error);
      stopMediaStreamTracks(stream);
      mediaRecorderRef.current = null;
      setIsRecording(false);
      setRecordingTime(0);
      recordingDurationRef.current = 0;
      clearInterval(recordingTimerRef.current);
      toast.error(error.name === 'NotAllowedError' ? 'Microphone permission is required' : 'Failed to start recording');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };
  
  const handleShareLocation = async () => {
    if (!selectedConversation) return;
    
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    
    toast.info('Getting your location...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // Stage the location for preview instead of sending immediately
        setStagedLocation({
          lat: latitude,
          lng: longitude,
          name: 'Current Location'
        });
        toast.success('Location ready to send');
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Failed to get location');
      }
    );
  };
  
  // Send staged location
  const sendStagedLocation = async () => {
    if (!stagedLocation || !selectedConversation) return;
    
    try {
      const response = await api.post('/messages', {
        conversation_id: selectedConversation.id,
        content: `Shared location: ${stagedLocation.lat.toFixed(5)}, ${stagedLocation.lng.toFixed(5)}`,
        type: 'location',
        location: { lat: stagedLocation.lat, lng: stagedLocation.lng },
        location_name: stagedLocation.name
      });
      
      setMessages(prev => [...prev, response.data]);
      setStagedLocation(null);
      toast.success('Location shared');
      fetchConversations();
    } catch (error) {
      console.error('Failed to share location:', error);
      toast.error('Failed to share location');
    }
  };
  
  const filteredConversations = useMemo(() => {
    const activityKey = (c) => c.last_message?.created_at || c.created_at || '';
    return conversations
      .filter(conv => {
        if (!searchQuery) return true;
        const name = conv.name || conv.participants.join(', ');
        return name.toLowerCase().includes(searchQuery.toLowerCase());
      })
      .sort((a, b) => activityKey(b).localeCompare(activityKey(a))) // newest activity first
      .sort((a, b) => (b.pinned === true) - (a.pinned === true));   // pinned stay on top
  }, [conversations, searchQuery]);

  const togglePinConversation = useCallback(async (conversationId, e) => {
    e?.stopPropagation?.();
    try {
      const { data } = await api.post(`/conversations/${conversationId}/pin`);
      setConversations(prev => prev.map(c => (c.id === conversationId ? { ...c, pinned: data.pinned } : c)));
    } catch (err) {
      toast.error('Could not update pin');
    }
  }, [api]);

  const muteConversation = useCallback(async (conversationId, duration, e) => {
    e?.stopPropagation?.();
    try {
      const { data } = await api.post(`/conversations/${conversationId}/mute`, { duration });
      setConversations(prev => prev.map(c => (c.id === conversationId ? { ...c, muted: data.muted, mute_expires_at: data.mute_expires_at } : c)));
      toast.success(data.muted ? 'Conversation muted' : 'Conversation unmuted');
    } catch (err) {
      toast.error('Could not update mute');
    }
  }, [api]);

  // Keep muted ids in localStorage so AuthContext can silence toasts/sounds app-wide.
  useEffect(() => {
    const mutedIds = conversations.filter(c => c.muted).map(c => c.id);
    localStorage.setItem('muted_conversation_ids', JSON.stringify(mutedIds));
  }, [conversations]);

  const activeTypingUsers = selectedConversation ? (typingMap[selectedConversation.id] || EMPTY_TYPING_USERS) : EMPTY_TYPING_USERS;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4 lg:space-y-6" data-testid="chat-page">
      {/* Page Header — hidden on mobile to maximize chat real estate */}
      <div className="hidden lg:flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2563eb]/10">
            <MessageSquare className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Chat</h1>
            <p className="text-sm text-muted-foreground">Real-time messaging and collaboration</p>
          </div>
        </div>
      </div>
      
      {/* Chat Interface — single-pane on mobile, two-pane on desktop */}
      <div className="flex flex-col lg:grid lg:grid-cols-[clamp(20rem,24vw,28rem)_minmax(0,1fr)] lg:gap-4 h-[calc(100dvh-9rem)] lg:h-[calc(100vh-16rem)] min-w-0">
        {/* Left Sidebar - Conversations.
            Mobile: shown only when no conversation is selected.
            Desktop: always visible as col-span-3. */}
        <div className={`${selectedConversation ? 'hidden lg:flex' : 'flex flex-1 lg:flex-none'} flex-col bg-card rounded-lg border border-border overflow-hidden min-h-0 min-w-0`} data-testid="chat-sidebar">
          {/* Search */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 text-base lg:h-9 lg:text-sm"
                data-testid="chat-conversation-search-input"
              />
            </div>
          </div>
          
          {/* New Conversation Button */}
          <div className="p-3 border-b border-border flex gap-2">
            <Dialog open={isNewChatOpen} onOpenChange={setIsNewChatOpen}>
              <DialogTrigger asChild>
                <Button className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8]" size="sm" data-testid="new-chat-button">
                  <Plus className="w-4 h-4 mr-2" />
                  New Chat
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>New Conversation</DialogTitle>
                  <DialogDescription>Start a direct or group conversation.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={newChatType === 'direct' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setNewChatType('direct')}
                        data-testid="new-chat-type-direct-button"
                      >
                        Direct
                      </Button>
                      {/* People (Driver/Staff/Passenger logins) can only create direct chats */}
                      {user?.role !== 'chat_contact' && (
                        <Button
                          variant={newChatType === 'group' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNewChatType('group')}
                          data-testid="new-chat-type-group-button"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Group
                        </Button>
                      )}
                    </div>
                  </div>
                  {newChatType === 'group' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Group Name</Label>
                        <Input
                          placeholder="e.g., Sydney Fleet Team"
                          value={newChatName}
                          onChange={(e) => setNewChatName(e.target.value)}
                          data-testid="new-chat-group-name-input"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">Allow members to leave</Label>
                          <p className="text-xs text-muted-foreground">When off, participants cannot leave this group</p>
                        </div>
                        <Checkbox
                          checked={newChatAllowLeave}
                          onCheckedChange={(checked) => setNewChatAllowLeave(!!checked)}
                          data-testid="allow-leave-toggle"
                        />
                      </div>
                    </div>
                  )}
                  <GroupColorPicker
                    value={newChatAvatarColor}
                    onChange={setNewChatAvatarColor}
                    presetColors={CHAT_AVATAR_COLOR_PRESETS}
                    showCustom={false}
                    testIdPrefix="chat-avatar-color"
                    label="Avatar colour"
                  />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label>
                        {newChatType === 'direct' ? 'Select 1 Participant' : 'Select Participants'}
                      </Label>
                      {newChatType === 'group' && tenantUsers.length > 0 && (() => {
                        const visibleGroups = buildParticipantGroups(tenantUsers, participantSearch);
                        const visibleIds = visibleGroups.flatMap(g => g.users.map(u => u.id));
                        if (visibleIds.length === 0) return null;
                        const allSelected = visibleIds.every(id => selectedParticipants.includes(id));
                        return (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => toggleSelectAllVisible(visibleIds)}
                            data-testid="new-chat-select-all-toggle"
                          >
                            {allSelected
                              ? `Deselect all (${visibleIds.length})`
                              : `Select all (${visibleIds.length})`}
                          </Button>
                        );
                      })()}
                    </div>
                    {/* Participant Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search participants..."
                        value={participantSearch}
                        onChange={(e) => setParticipantSearch(e.target.value)}
                        className="pl-9 h-10 text-base lg:h-9 lg:text-sm"
                        data-testid="new-chat-participant-search-input"
                      />
                    </div>
                    <ScrollArea className="h-48 border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
                      {tenantUsers.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No users available</p>
                      ) : (() => {
                        const groups = buildParticipantGroups(tenantUsers, participantSearch);
                        if (groups.length === 0) {
                          return <p className="text-sm text-muted-foreground text-center py-4">No matching users</p>;
                        }
                        return (
                          <div className="space-y-3 p-3">
                            {groups.map(({ group, users }) => (
                              <div key={group} className="space-y-2" data-testid={`participant-group-${group.toLowerCase().replace(/\s+/g, '-')}`}>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
                                {users.map(u => (
                                  <div key={u.id} className="flex items-center space-x-2 min-h-[28px]">
                                    <Checkbox
                                      id={u.id}
                                      checked={selectedParticipants.includes(u.id)}
                                      onCheckedChange={() => toggleParticipant(u.id)}
                                      className="flex-shrink-0"
                                      data-testid={`participant-option-${u.id}`}
                                    />
                                    <Avatar className="h-6 w-6 shrink-0">
                                      {avatarUrls[u.id] && <AvatarImage src={avatarUrls[u.id]} className="object-cover" />}
                                      <AvatarFallback className="bg-[#2563eb] text-white text-[10px]">
                                        {(u.full_name || u.email)?.[0]?.toUpperCase() || 'U'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <label
                                      htmlFor={u.id}
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                                    >
                                      {u.full_name || u.email} <span className="text-xs text-muted-foreground">({u.role})</span>
                                    </label>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </ScrollArea>
                    <p className="text-xs text-muted-foreground">
                      {selectedParticipants.length} participant{selectedParticipants.length !== 1 ? 's' : ''} selected
                      {newChatType === 'direct' && ' (max 1)'}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setIsNewChatOpen(false);
                    setParticipantSearch('');
                    setNewChatAllowLeave(true);
                  }}>Cancel</Button>
                  <Button onClick={handleCreateConversation} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="new-chat-create-button">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button
              variant="outline"
              size="sm"
              className="px-2.5 shrink-0 relative"
              onClick={openCallHistory}
              title="Call history"
              data-testid="call-history-button"
            >
              <History className="w-4 h-4" />
              {missedCallCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
                  data-testid="missed-call-badge"
                >
                  {missedCallCount > 9 ? '9+' : missedCallCount}
                </span>
              )}
            </Button>
          </div>
          
          {/* Selection Mode Controls */}
          {conversations.some(c => c.created_by === user?.id) && (
            <div className="px-3 py-2 border-b border-border flex items-center justify-between">
              {isSelectionMode ? (
                <>
                  <span className="text-xs text-muted-foreground">
                    {selectedConversations.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsSelectionMode(false);
                        setSelectedConversations([]);
                        setDeleteSummary(null);
                        setIsDeleteDialogOpen(false);
                      }}
                      className="h-7 text-xs"
                    >
                      Cancel
                    </Button>
                    <AlertDialog
                      open={isDeleteDialogOpen}
                      onOpenChange={(open) => {
                        setIsDeleteDialogOpen(open);
                        if (open) fetchDeleteSummary(selectedConversations);
                        else setDeleteSummary(null);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={selectedConversations.length === 0}
                          className="h-7 text-xs"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete ({selectedConversations.length})
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Conversations?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete {selectedConversations.length} conversation{selectedConversations.length > 1 ? 's' : ''}? 
                            This will permanently delete all messages and cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-950 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-100" data-testid="delete-conversations-summary">
                          {deleteSummaryLoading ? (
                            <div className="flex items-center gap-2 text-muted-foreground" data-testid="delete-conversations-summary-loading">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Checking files and messages...
                            </div>
                          ) : deleteSummary ? (
                            <div className="space-y-1">
                              <p className="font-medium" data-testid="delete-conversations-summary-files">
                                This will remove {deleteSummary.total_messages} message{deleteSummary.total_messages === 1 ? '' : 's'} and {deleteSummary.total_files} file{deleteSummary.total_files === 1 ? '' : 's'} from storage.
                              </p>
                              <p className="text-xs opacity-80">Files are permanently deleted after you confirm.</p>
                            </div>
                          ) : (
                            <p className="text-muted-foreground">File summary unavailable. Delete will still clean scoped chat files when possible.</p>
                          )}
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteSelectedConversations}
                            disabled={deleteSummaryLoading}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="delete-conversations-confirm-button"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsSelectionMode(true)}
                  className="h-7 text-xs text-muted-foreground hover:text-foreground w-full justify-start"
                >
                  <Trash2 className="w-3 h-3 mr-1 text-red-500" />
                  Select to delete
                </Button>
              )}
            </div>
          )}
          
          {/* Conversations List */}
          <ScrollArea className="flex-1 overflow-x-hidden">
            <div className="p-2 space-y-1 min-w-0 max-w-full overflow-x-hidden">
              {filteredConversations.map((conversation) => {
                // The open conversation is being read right now — never show a badge
                // for it (fixes the stale '1 unread' race with the read receipt).
                const unreadCount = conversation.id === selectedConversation?.id
                  ? 0
                  : (conversation.unread_count?.[user?.id] || 0);
                const isSelected = selectedConversations.includes(conversation.id);
                
                return (
                  <div
                    key={conversation.id}
                    role="button"
                    tabIndex={0}
                    data-testid={`conversation-item-${conversation.id}`}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.currentTarget.click();
                      }
                    }}
                    onClick={async () => {
                      if (isSelectionMode) {
                        // Only allow selecting conversations the user created
                        if (conversation.created_by === user?.id) {
                          toggleConversationSelection(conversation.id, { stopPropagation: () => {} });
                        }
                        return;
                      }
                      document.activeElement?.blur?.();
                      saveCurrentMessageScrollPosition();
                      setSelectedConversation(conversation);
                    }}
                    className={`w-full max-w-full overflow-hidden p-3 rounded-lg cursor-pointer transition-all ${
                      isSelectionMode && isSelected
                        ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-500'
                        : selectedConversation?.id === conversation.id
                        ? 'bg-[#2563eb]/10 border-l-4 border-l-[#2563eb]'
                        : 'hover:bg-muted border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0 w-full max-w-full overflow-hidden pt-1 -mt-1 pl-1 -ml-1">
                      {/* Checkbox in selection mode — only for conversations user created */}
                      {isSelectionMode && conversation.created_by === user?.id && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleConversationSelection(conversation.id, { stopPropagation: () => {} })}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-shrink-0"
                        />
                      )}
                      <div className="relative shrink-0">
                        <Avatar className="h-10 w-10">
                          {conversation.type === 'direct' && avatarUrls[otherParticipantId(conversation)] && (
                            <AvatarImage src={avatarUrls[otherParticipantId(conversation)]} className="object-cover" data-testid={`conversation-avatar-photo-${conversation.id}`} />
                          )}
                          <AvatarFallback
                            className="font-semibold"
                            style={{ backgroundColor: getAvatarColorHex(conversation), color: getAvatarForegroundColor(getAvatarColorHex(conversation)) }}
                            data-testid={`conversation-avatar-${conversation.id}`}
                          >
                            {conversation.type === 'group' ? <Users className="w-5 h-5" /> : conversation.name?.[0] || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {unreadCount > 0 && !isSelectionMode && (
                          <div
                            className="absolute -top-1 -left-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1"
                            data-testid={`conversation-unread-badge-${conversation.id}`}
                          >
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 w-0 min-w-0 max-w-full overflow-hidden">
                        <div className="flex items-center justify-between gap-2 mb-1 min-w-0 max-w-full overflow-hidden">
                          <p className="block font-medium text-sm truncate flex-1 w-0 min-w-0 max-w-full" title={conversation.name || `${conversation.type} Chat`}>
                            {conversation.name || `${conversation.type} Chat`}
                          </p>
                          <span className="flex items-center gap-1 shrink-0">
                            {!isSelectionMode && (conversation.muted ? (
                              <button
                                type="button"
                                onClick={(e) => muteConversation(conversation.id, 'off', e)}
                                title={conversation.mute_expires_at ? `Muted until ${formatDT(conversation.mute_expires_at)} — click to unmute` : 'Muted — click to unmute'}
                                aria-label="Unmute conversation"
                                className="p-0.5 rounded transition-colors text-amber-500 dark:text-amber-400"
                                data-testid={`conversation-mute-${conversation.id}`}
                              >
                                <BellOff className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Mute notifications"
                                    aria-label="Mute conversation"
                                    className="p-0.5 rounded transition-colors text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-white"
                                    data-testid={`conversation-mute-${conversation.id}`}
                                  >
                                    <BellOff className="w-3.5 h-3.5" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  <DropdownMenuItem onClick={(e) => muteConversation(conversation.id, '1h', e)} data-testid={`mute-1h-${conversation.id}`}>Mute for 1 hour</DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => muteConversation(conversation.id, '1d', e)} data-testid={`mute-1d-${conversation.id}`}>Mute for 1 day</DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => muteConversation(conversation.id, '1w', e)} data-testid={`mute-1w-${conversation.id}`}>Mute for 1 week</DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => muteConversation(conversation.id, 'forever', e)} data-testid={`mute-forever-${conversation.id}`}>Mute until I turn it off</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ))}
                            {!isSelectionMode && (
                              <button
                                type="button"
                                onClick={(e) => togglePinConversation(conversation.id, e)}
                                title={conversation.pinned ? 'Unpin' : 'Pin to top'}
                                aria-label={conversation.pinned ? 'Unpin conversation' : 'Pin conversation'}
                                className={`p-0.5 rounded transition-colors ${conversation.pinned ? 'text-[#2563eb] dark:text-blue-400' : 'text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-white'}`}
                                data-testid={`conversation-pin-${conversation.id}`}
                              >
                                <Pin className={`w-3.5 h-3.5 ${conversation.pinned ? 'fill-current' : ''}`} />
                              </button>
                            )}
                            {conversation.last_message && !isSelectionMode && (
                              <span className="text-xs text-muted-foreground">
                                {(() => {
                                  let ts = conversation.last_message.created_at;
                                  if (ts && !ts.endsWith('Z') && !ts.includes('+')) ts += 'Z';
                                  return formatTimeOnly(ts);
                                })()}
                              </span>
                            )}
                          </span>
                        </div>
                        {(() => {
                          const draftText = drafts?.[conversation.id];
                          if (draftText) {
                            return (
                              <p
                                className="block w-full max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground"
                                title={`Draft: ${draftText}`}
                                data-testid={`conversation-draft-preview-${conversation.id}`}
                              >
                                <span className="text-[#dc2626] font-semibold">Draft:</span> {draftText}
                              </p>
                            );
                          }
                          if (conversation.last_message) {
                            return (
                              <p className="block w-full max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground" title={`${conversation.last_message.sender_name}: ${conversation.last_message.content}`}>
                                {conversation.last_message.sender_name}: {conversation.last_message.content}
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredConversations.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No conversations</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
        
        {/* Main Chat Area */}
        <div
          className={`${selectedConversation ? 'flex flex-1 lg:flex-none' : 'hidden lg:flex'} relative flex-col bg-card rounded-lg border border-border overflow-hidden min-h-0 min-w-0`}
          data-testid="chat-thread-pane"
          onDragEnter={handleChatDragEnter}
          onDragOver={handleChatDragOver}
          onDragLeave={handleChatDragLeave}
          onDrop={handleChatDrop}
        >
          {isDraggingFiles && selectedConversation && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm border-2 border-dashed border-[#2563eb] rounded-lg" data-testid="chat-drop-overlay">
              <div className="flex flex-col items-center gap-3 text-center px-6">
                <div className="h-12 w-12 rounded-full bg-[#2563eb]/10 text-[#2563eb] flex items-center justify-center">
                  <Upload className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold">Drop files to attach</p>
                  <p className="text-sm text-muted-foreground" data-testid="chat-drop-overlay-summary">
                    {dragFileSummary.count > 0
                      ? `${dragFileSummary.count} ${dragFileSummary.label} ready to stage`
                      : 'Files ready to stage'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">They’ll upload only when you press Send.</p>
                </div>
              </div>
            </div>
          )}
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-3 lg:p-4 border-b border-border flex items-center gap-2 lg:gap-3 min-w-0">
                <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                  {/* Mobile back button — returns to conversation list */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden h-9 w-9 shrink-0 [&_svg]:size-5"
                    onClick={() => setSelectedConversation(null)}
                    data-testid="chat-back-button"
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft />
                  </Button>
                  <Avatar className="h-10 w-10 shrink-0">
                    {selectedConversation.type === 'direct' && avatarUrls[otherParticipantId(selectedConversation)] && (
                      <AvatarImage src={avatarUrls[otherParticipantId(selectedConversation)]} className="object-cover" data-testid="chat-header-avatar-photo" />
                    )}
                    <AvatarFallback
                      className="font-semibold"
                      style={{ backgroundColor: getAvatarColorHex(selectedConversation), color: getAvatarForegroundColor(getAvatarColorHex(selectedConversation)) }}
                      data-testid="chat-header-avatar"
                    >
                      {selectedConversation.type === 'group' ? <Users className="w-5 h-5" /> : selectedConversation.name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm sm:text-base leading-tight overflow-hidden break-words [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]" data-testid="chat-header-title">
                      {selectedConversation.name || `${selectedConversation.type} Chat`}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate" data-testid="chat-header-participants">
                      {selectedConversation.participants.length} participant{selectedConversation.participants.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                  {/* Voice / video call — direct conversations only, gated by per-conversation
                      capacity (either side's privileges; falls back to own limits) */}
                  {selectedConversation.type === 'direct' && (selectedConversation.call_limits || callLimits).voice_max >= 2 && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Voice call"
                        onClick={() => startCall(selectedConversation, selectedConversation.name)}
                        data-testid="chat-voice-call-button"
                      >
                        <Phone className="w-5 h-5 text-green-600" />
                      </Button>
                      {(selectedConversation.call_limits || callLimits).video_max >= 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Video call"
                          onClick={() => startCall(selectedConversation, selectedConversation.name, 'video')}
                          data-testid="chat-video-call-button"
                        >
                          <Video className="w-5 h-5 text-green-600" />
                        </Button>
                      )}
                    </>
                  )}
                  {/* Group voice/video call — Option A: the initiator's own tier must cover the group size */}
                  {selectedConversation.type === 'group' && (() => {
                    const limits = selectedConversation.call_limits || callLimits;
                    const count = (selectedConversation.participants || []).length;
                    const canVoice = limits.voice_max >= 4 && limits.voice_max >= count;
                    const canVideo = limits.video_max >= 4 && limits.video_max >= count;
                    if (activeConvCalls[selectedConversation.id]) return null; // join banner shown instead
                    return (
                      <>
                        {canVoice && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Group voice call"
                            onClick={() => startGroupCall(selectedConversation, 'voice')}
                            data-testid="group-voice-call-button"
                          >
                            <Phone className="w-5 h-5 text-green-600" />
                          </Button>
                        )}
                        {canVideo && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Group video call"
                            onClick={() => startGroupCall(selectedConversation, 'video')}
                            data-testid="group-video-call-button"
                          >
                            <Video className="w-5 h-5 text-green-600" />
                          </Button>
                        )}
                      </>
                    );
                  })()}
                  {/* Add Members Button - Only for group chats, only creator */}
                  {selectedConversation.type === 'group' && selectedConversation.created_by === user?.id && (
                    <Dialog open={isAddParticipantsOpen} onOpenChange={setIsAddParticipantsOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="icon" title="Add members" data-testid="add-group-members-button">
                          <UserPlus className="w-5 h-5" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Members to Group</DialogTitle>
                          <DialogDescription>Select users to add to this group conversation.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          {/* Search */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search users..."
                              value={addParticipantSearch}
                              onChange={(e) => setAddParticipantSearch(e.target.value)}
                              className="pl-9 h-9 text-sm"
                            />
                          </div>
                          
                          {/* User List */}
                          <ScrollArea className="h-48 border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
                            {availableParticipants.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-4">No more users to add</p>
                            ) : (() => {
                              const groups = buildParticipantGroups(availableParticipants, addParticipantSearch);
                              if (groups.length === 0) {
                                return <p className="text-sm text-muted-foreground text-center py-4">No matching users</p>;
                              }
                              return (
                                <div className="space-y-3 p-3">
                                  {groups.map(({ group, users }) => (
                                    <div key={group} className="space-y-2">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
                                      {users.map(u => (
                                        <div key={u.id} className="flex items-center space-x-2 min-h-[28px]">
                                          <Checkbox
                                            id={`add-${u.id}`}
                                            checked={newParticipantsToAdd.includes(u.id)}
                                            onCheckedChange={() => {
                                              setNewParticipantsToAdd(prev =>
                                                prev.includes(u.id)
                                                  ? prev.filter(id => id !== u.id)
                                                  : [...prev, u.id]
                                              );
                                            }}
                                            className="flex-shrink-0"
                                          />
                                          <label
                                            htmlFor={`add-${u.id}`}
                                            className="text-sm font-medium leading-none cursor-pointer flex-1"
                                          >
                                            {u.full_name || u.email} <span className="text-xs text-muted-foreground">({u.role})</span>
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </ScrollArea>
                          
                          <p className="text-xs text-muted-foreground">
                            {newParticipantsToAdd.length} user{newParticipantsToAdd.length !== 1 ? 's' : ''} selected
                          </p>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => {
                            setIsAddParticipantsOpen(false);
                            setNewParticipantsToAdd([]);
                            setAddParticipantSearch('');
                          }}>Cancel</Button>
                          <Button 
                            onClick={handleAddParticipants} 
                            disabled={newParticipantsToAdd.length === 0}
                            className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                          >
                            Add Members
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {/* Manage Members Button - Only for group chats */}
                  {selectedConversation.type === 'group' && (
                    <Dialog open={isManageParticipantsOpen} onOpenChange={setIsManageParticipantsOpen}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="icon" title="Manage members" data-testid="manage-group-members-button">
                          <Users className="w-5 h-5" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Group Members ({selectedConversation.participants.length})</DialogTitle>
                          <DialogDescription>Manage participants in this group.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-2">
                          <ScrollArea className="h-64 border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
                            <div className="p-3 space-y-2">
                              {[...selectedConversation.participants]
                                .sort((a, b) => {
                                  // Sort creator first, then alphabetically
                                  if (a === selectedConversation.created_by) return -1;
                                  if (b === selectedConversation.created_by) return 1;
                                  const nameA = (getParticipantInfo(a).name || '').toLowerCase();
                                  const nameB = (getParticipantInfo(b).name || '').toLowerCase();
                                  return nameA.localeCompare(nameB);
                                })
                                .map(participantId => {
                                const { name, role } = getParticipantInfo(participantId);
                                const isCurrentUser = participantId === user?.id;
                                const isCreator = participantId === selectedConversation.created_by;
                                const iAmCreator = selectedConversation.created_by === user?.id;
                                const canLeave = selectedConversation.allow_leave !== false;
                                
                                // Determine if action button should show
                                let showAction = false;
                                let actionLabel = '';
                                if (selectedConversation.participants.length > 2) {
                                  if (isCurrentUser && !isCreator && canLeave) {
                                    showAction = true;
                                    actionLabel = 'Leave';
                                  } else if (!isCurrentUser && iAmCreator && !isCreator) {
                                    showAction = true;
                                    actionLabel = 'Remove';
                                  } else if (isCurrentUser && isCreator) {
                                    showAction = true;
                                    actionLabel = 'Leave';
                                  }
                                }
                                
                                return (
                                  <div key={participantId} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted">
                                    <div className="flex items-center gap-3">
                                      <Avatar className="h-8 w-8">
                                        {avatarUrls[participantId] && <AvatarImage src={avatarUrls[participantId]} className="object-cover" />}
                                        <AvatarFallback className="bg-[#2563eb] text-white text-xs">
                                          {name?.[0]?.toUpperCase() || 'U'}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div>
                                        <p className="text-sm font-medium flex items-center gap-2">
                                          {name} {isCurrentUser && <span className="text-muted-foreground">(You)</span>}
                                          {isCreator && (
                                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500 text-amber-600 dark:text-amber-400">
                                              Creator
                                            </Badge>
                                          )}
                                        </p>
                                        {role && <p className="text-xs text-muted-foreground">{role}</p>}
                                      </div>
                                    </div>
                                    {showAction && (
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                        onClick={() => {
                                          setIsManageParticipantsOpen(false);
                                          setConfirmRemoveParticipant({ id: participantId, name, action: actionLabel });
                                        }}
                                      >
                                        {actionLabel}
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                          {selectedConversation.participants.length <= 2 && (
                            <p className="text-xs text-muted-foreground text-center">
                              Group must have at least 2 members
                            </p>
                          )}
                          {/* Allow Leave toggle — only visible to creator */}
                          {selectedConversation.created_by === user?.id && (
                            <div className="flex items-center justify-between pt-2 border-t">
                              <div>
                                <p className="text-sm font-medium">Allow members to leave</p>
                                <p className="text-xs text-muted-foreground">When off, participants cannot leave this group</p>
                              </div>
                              <Checkbox
                                checked={selectedConversation.allow_leave !== false}
                                onCheckedChange={async (checked) => {
                                  try {
                                    await api.patch(`/conversations/${selectedConversation.id}/settings`, { allow_leave: !!checked });
                                    setSelectedConversation(prev => ({ ...prev, allow_leave: !!checked }));
                                    setConversations(prev => prev.map(c => c.id === selectedConversation.id ? { ...c, allow_leave: !!checked } : c));
                                    toast.success(checked ? 'Members can now leave' : 'Members cannot leave');
                                  } catch (e) {
                                    toast.error('Failed to update setting');
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsManageParticipantsOpen(false)}>
                            Close
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}

                  {canEditConversationDetails(selectedConversation) && (
                    <Dialog open={isEditConversationOpen} onOpenChange={setIsEditConversationOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={selectedConversation.type === 'group' ? 'Edit group details' : 'Edit avatar colour'}
                          onClick={openEditConversationDialog}
                          data-testid="edit-conversation-details-button"
                        >
                          <Pencil className="w-5 h-5" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {selectedConversation.type === 'group' ? 'Edit Group Chat' : 'Edit Avatar Colour'}
                          </DialogTitle>
                          <DialogDescription>
                            {selectedConversation.type === 'group'
                              ? 'Update this group chat name and avatar colour.'
                              : 'Update the avatar colour for this direct chat.'}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                          {selectedConversation.type === 'group' && (
                            <div className="space-y-2">
                              <Label>Group Chat Name</Label>
                              <Input
                                value={editConversationName}
                                onChange={(event) => setEditConversationName(event.target.value)}
                                placeholder="Group chat name"
                                data-testid="edit-conversation-name-input"
                              />
                            </div>
                          )}
                          <GroupColorPicker
                            value={editConversationAvatarColor}
                            onChange={setEditConversationAvatarColor}
                            presetColors={CHAT_AVATAR_COLOR_PRESETS}
                            showCustom={false}
                            testIdPrefix="edit-chat-avatar-color"
                            label="Avatar colour"
                          />
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsEditConversationOpen(false)} data-testid="edit-conversation-cancel-button">
                            Cancel
                          </Button>
                          <Button onClick={handleSaveConversationDetails} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="edit-conversation-save-button">
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {/* More Options Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid="chat-more-options-button">
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExportMessages('csv')}>
                        <Upload className="w-4 h-4 mr-2" />
                        Export as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportMessages('json')}>
                        <FileText className="w-4 h-4 mr-2" />
                        Export as JSON
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportMessages('pdf')}>
                        <FileText className="w-4 h-4 mr-2" />
                        Export as PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Ongoing group call — join banner */}
              {selectedConversation.type === 'group' && activeConvCalls[selectedConversation.id]
                && groupCall?.call_id !== activeConvCalls[selectedConversation.id].call_id && (
                <div className="px-4 py-2 bg-green-600/10 border-b border-green-600/30 flex items-center justify-between gap-3" data-testid="group-call-banner">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                    <span className="text-sm truncate">
                      Group {activeConvCalls[selectedConversation.id].media === 'video' ? 'video' : 'voice'} call in progress
                    </span>
                  </div>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white h-8"
                    onClick={() => joinGroupCall(activeConvCalls[selectedConversation.id].call_id, selectedConversation)}
                    data-testid="group-call-banner-join"
                  >
                    <Phone className="w-4 h-4 mr-1.5" />
                    Join
                  </Button>
                </div>
              )}

              {/* Messages */}
              <div className="relative flex-1 min-h-0">
                <ScrollArea ref={messagesScrollAreaRef} className="h-full p-3 lg:p-4 min-w-0">
                  <ChatMessageList
                    messages={messages}
                    userId={user?.id}
                    isAdmin={isCurrentUserAdmin}
                    canModerate={selectedConversation.type === 'group' && selectedConversation.created_by === user?.id}
                    firstUnreadMessageId={firstUnreadMessageId}
                    typingUsers={activeTypingUsers}
                    loadingOlder={loadingOlder}
                    openReactionMenuId={openReactionMenuId}
                    setOpenReactionMenuId={setOpenReactionMenuId}
                    onToggleReaction={handleToggleReaction}
                    onOpenLightbox={setLightboxFile}
                    onDeleteMessage={setMessageToDelete}
                    onDownload={handleDownloadAttachment}
                    messagesEndRef={messagesEndRef}
                    contentRef={messagesContentRef}
                    showSenderAvatars={selectedConversation.type === 'group'}
                    avatarUrls={avatarUrls}
                  />
                </ScrollArea>

                {jumpToBottomState.visible && (
                  <Button
                    type="button"
                    size="icon"
                    onClick={handleJumpToBottom}
                    className="absolute bottom-4 right-4 z-20 h-11 w-11 rounded-full bg-[#2563eb] text-white shadow-lg shadow-[#2563eb]/30 hover:bg-[#1d4ed8] transition-[opacity,transform,background-color] duration-200"
                    aria-label="Jump to latest messages"
                    title="Jump to latest messages"
                    data-testid="chat-jump-to-bottom-button"
                  >
                    <ArrowDown className="w-5 h-5" />
                    {jumpToBottomState.unreadBelow > 0 && (
                      <Badge
                        className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full bg-red-500 px-1.5 text-[10px] text-white border-2 border-background"
                        data-testid="chat-jump-to-bottom-unread-badge"
                      >
                        {jumpToBottomState.unreadBelow > 99 ? '99+' : jumpToBottomState.unreadBelow}
                      </Badge>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Message Input */}
              <div className="p-4 border-t border-border">
                {uploading && (
                  <div className="mb-2 p-2 bg-muted rounded-lg text-sm">
                    Uploading files...
                  </div>
                )}
                {uploadProgress && (
                  <div className="mb-2 rounded-lg border border-[#2563eb]/20 bg-[#2563eb]/5 px-3 py-2" data-testid="chat-upload-progress-strip">
                    <div className="flex items-center justify-between gap-3 text-xs mb-1">
                      <span className="font-medium truncate" data-testid="chat-upload-progress-filename">
                        {uploadProgress.isVideo ? 'Uploading video' : 'Uploading file'}: {uploadProgress.fileName}
                      </span>
                      <span className="shrink-0 text-muted-foreground" data-testid="chat-upload-progress-percent">
                        {uploadProgress.index}/{uploadProgress.total} · {uploadProgress.percent}%
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-[#2563eb]/15">
                      <div
                        className="h-full rounded-full bg-[#2563eb] transition-[width] duration-150 ease-out"
                        style={{ width: `${uploadProgress.percent}%` }}
                        data-testid="chat-upload-progress-bar"
                      />
                    </div>
                  </div>
                )}
                
                {/* File Previews */}
                {selectedFiles.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2" data-testid="chat-staged-files-list">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 text-sm" data-testid={`chat-staged-file-${index}`}>
                        <Paperclip className="w-4 h-4" />
                        <span className="truncate max-w-32" data-testid={`chat-staged-file-name-${index}`}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="hover:text-red-500"
                          data-testid={`chat-staged-file-remove-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Audio Recording Preview */}
                {recordedAudio && (
                  <div className="mb-2 flex items-center gap-2 bg-muted rounded-lg p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium mb-2">Voice Message ({recordedAudio.duration}s)</p>
                      <VoiceMessagePlayer
                        src={recordedAudio.url}
                        duration={recordedAudio.duration}
                        isOwn={false}
                        testId="voice-preview-player"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        URL.revokeObjectURL(recordedAudio.url);
                        setRecordedAudio(null);
                      }}
                      className="hover:text-red-500 shrink-0"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                {/* Location Preview */}
                {stagedLocation && (
                  <div className="mb-2 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                    <MapPin className="w-5 h-5 text-blue-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">📍 {stagedLocation.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {stagedLocation.lat.toFixed(5)}, {stagedLocation.lng.toFixed(5)}
                      </p>
                    </div>
                    <a
                      href={`https://www.google.com/maps?q=${stagedLocation.lat},${stagedLocation.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline mr-2"
                    >
                      Preview
                    </a>
                    <button
                      type="button"
                      onClick={() => setStagedLocation(null)}
                      className="hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                {isRecording && (
                  <div className="mb-2 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-sm">Recording... {recordingTime}s</span>
                    </div>
                    <Button size="sm" variant="destructive" onClick={stopRecording}>
                      Stop
                    </Button>
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="flex items-end gap-2 min-w-0" data-testid="chat-message-form">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*,.pdf,audio/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                  />
                  <div className="relative shrink-0 sm:hidden">
                    {isMobileActionsOpen && (
                      <div className="absolute bottom-full left-0 mb-2 z-20 flex flex-col gap-2" data-testid="chat-mobile-actions-menu">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            fileInputRef.current?.click();
                            setIsMobileActionsOpen(false);
                          }}
                          disabled={uploading || isRecording}
                          className="bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600"
                          data-testid="chat-mobile-attach-button"
                        >
                          <Paperclip className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            isRecording ? stopRecording() : startRecording();
                            setIsMobileActionsOpen(false);
                          }}
                          disabled={uploading}
                          className={isRecording ? 'bg-red-100 text-red-700 border-red-300 shadow-sm dark:bg-red-900/50 dark:text-red-50 dark:border-red-600 dark:shadow-black/20' : 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600'}
                          data-testid="chat-mobile-record-button"
                        >
                          <Mic className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            handleShareLocation();
                            setIsMobileActionsOpen(false);
                          }}
                          disabled={uploading || isRecording}
                          className="bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600"
                          data-testid="chat-mobile-location-button"
                        >
                          <MapPin className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setIsMobileActionsOpen(prev => !prev)}
                      disabled={uploading}
                      className={isMobileActionsOpen ? 'bg-slate-300 text-slate-900 border-slate-400 shadow-sm dark:bg-slate-600 dark:text-white dark:border-slate-400 dark:shadow-black/20' : 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600'}
                      aria-expanded={isMobileActionsOpen}
                      data-testid="chat-mobile-actions-toggle"
                    >
                      {isMobileActionsOpen ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="hidden shrink-0 gap-2 sm:flex">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || isRecording}
                      className="bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600"
                      data-testid="chat-attach-button"
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={isRecording ? stopRecording : startRecording}
                      disabled={uploading}
                      className={isRecording ? 'bg-red-100 text-red-700 border-red-300 shadow-sm dark:bg-red-900/50 dark:text-red-50 dark:border-red-600 dark:shadow-black/20' : 'bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600'}
                      data-testid="chat-record-button"
                    >
                      <Mic className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleShareLocation}
                      disabled={uploading || isRecording}
                      className="bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600"
                      data-testid="chat-location-button"
                    >
                      <MapPin className="w-4 h-4" />
                    </Button>
                    <Popover open={isEmojiPickerOpen} onOpenChange={setIsEmojiPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          disabled={uploading || isRecording}
                          className="bg-slate-100 text-slate-800 border-slate-300 shadow-sm hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-50 dark:border-slate-500 dark:shadow-black/20 dark:hover:bg-slate-600"
                          data-testid="chat-emoji-picker-button"
                          title="Open emoji picker"
                        >
                          <Smile className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" side="top" className="w-80 p-3" data-testid="chat-emoji-picker">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold">Emoji</p>
                            <p className="text-xs text-muted-foreground">Click to insert</p>
                          </div>
                          {EMOJI_PICKER_GROUPS.map(group => (
                            <div key={group.label} className="space-y-1">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{group.label}</p>
                              <div className="grid grid-cols-12 gap-1">
                                {group.emojis.map((emoji) => (
                                  <button
                                    key={`${group.label}-${emoji}`}
                                    type="button"
                                    onClick={() => handleInsertEmoji(emoji)}
                                    className="h-8 w-8 rounded-md text-xl leading-none hover:bg-muted focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30"
                                    data-testid={`chat-emoji-option-${emoji.codePointAt(0).toString(16)}`}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {/* Message input with @mentions autocomplete */}
                  <div className="flex-1 min-w-0 relative">
                    <div className="pointer-events-none absolute left-3 top-[22px] z-10 -translate-y-1/2 text-xs font-semibold text-muted-foreground/70" aria-hidden="true">
                      @
                    </div>
                    <Textarea
                      key={`composer-${selectedConversation.id}`}
                      ref={inputRef}
                      placeholder="Type a message..."
                      aria-label="Type a message. Use @ to mention someone."
                      defaultValue={messageInput}
                      onChange={handleMessageInputChange}
                      onKeyDown={handleMentionKeyDown}
                      rows={1}
                      className="w-full min-h-[44px] max-h-[42dvh] resize-none overflow-y-auto whitespace-pre-wrap break-words [overflow-wrap:anywhere] pl-8 pr-4 py-3 leading-5 bg-slate-100 border-slate-300 text-slate-950 shadow-sm placeholder:text-slate-500 focus:bg-slate-100 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 dark:bg-slate-950 dark:border-slate-500 dark:text-slate-50 dark:shadow-black/30 dark:placeholder:text-slate-400 dark:focus:bg-slate-950 dark:focus:border-[#60a5fa] dark:focus:ring-[#60a5fa]/20 transition-[height,box-shadow,border-color,background-color] duration-150 ease-out"
                      disabled={sending || uploading || isRecording}
                      data-testid="chat-message-input"
                    />
                    
                    {/* @Mentions autocomplete dropdown */}
                    {showMentions && mentionSuggestions.length > 0 && (
                      <div className="absolute bottom-full left-0 mb-1 w-64 bg-background border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto" data-testid="chat-mention-suggestions">
                        {mentionSuggestions.map(([userId, userName], index) => (
                          <button
                            key={userId}
                            type="button"
                            onClick={() => handleSelectMention(userId, userName)}
                            className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-muted transition-colors ${
                              index === selectedMentionIndex ? 'bg-muted' : ''
                            }`}
                            data-testid={`chat-mention-option-${userId}`}
                          >
                            <AtSign className="w-4 h-4 text-[#2563eb]" />
                            <span className="text-sm">{userName}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={(!hasComposerText && selectedFiles.length === 0 && !recordedAudio && !stagedLocation) || sending || uploading || isRecording}
                    className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                    data-testid="chat-send-button"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Standalone Remove/Leave Participant Confirmation — outside Dialog to avoid focus trap conflicts */}
      <AlertDialog open={!!confirmRemoveParticipant} onOpenChange={(open) => { if (!open) setConfirmRemoveParticipant(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmRemoveParticipant?.action === 'Leave' ? 'Leave Group' : 'Remove Member'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRemoveParticipant?.action === 'Leave'
                ? 'Are you sure you want to leave this group? You will no longer receive messages.'
                : `Are you sure you want to remove ${confirmRemoveParticipant?.name} from this group?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmRemoveParticipant(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmRemoveParticipant) {
                  handleRemoveParticipant(confirmRemoveParticipant.id);
                }
                setConfirmRemoveParticipant(null);
              }}
              className="bg-red-500 hover:bg-red-600"
            >
              {confirmRemoveParticipant?.action || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Lightbox: full-screen image / PDF viewer ── */}
      {lightboxFile && (
        <div
          className="fixed inset-0 z-[2000] bg-black/90 flex items-center justify-center"
          data-testid="chat-lightbox"
          onClick={() => setLightboxFile(null)}
          role="dialog"
          aria-modal="true"
        >
          {/* Top toolbar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between gap-2 px-3 py-2 bg-black/60 text-white" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-medium truncate min-w-0 flex-1" data-testid="chat-lightbox-name">{lightboxFile.name}</p>
            <a
              href={lightboxFile.url}
              download={lightboxFile.name}
              rel="noopener noreferrer"
              onClick={handleLightboxDownload}
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="chat-lightbox-download"
              aria-label="Download file"
            >
              <Download className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={() => setLightboxFile(null)}
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="chat-lightbox-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="w-full h-full pt-14 pb-4 px-2 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {lightboxFile.type === 'image' && (
              <img
                src={lightboxFile.url}
                alt={lightboxFile.name}
                className="max-w-full max-h-full object-contain"
                data-testid="chat-lightbox-image"
              />
            )}
            {lightboxFile.type === 'video' && (
              <video
                src={lightboxFile.url}
                controls
                autoPlay
                playsInline
                className="max-w-full max-h-full object-contain bg-black rounded-lg"
                data-testid="chat-lightbox-video"
              >
                Your browser does not support video playback.
              </video>
            )}
            {lightboxFile.type === 'pdf' && (
              <iframe
                src={lightboxFile.url}
                title={lightboxFile.name}
                className="w-full h-full bg-white rounded-lg"
                data-testid="chat-lightbox-pdf"
              />
            )}
          </div>
        </div>
      )}

      {/* Call history */}
      <CallHistoryPanel
        open={isCallHistoryOpen}
        onOpenChange={setIsCallHistoryOpen}
        onOpenConversation={(cid) => {
          const conv = conversations.find(c => c.id === cid);
          if (conv) setSelectedConversation(conv);
        }}
      />

      {/* Delete-message confirmation */}
      <AlertDialog open={!!messageToDelete} onOpenChange={(open) => { if (!open) setMessageToDelete(null); }}>
        <AlertDialogContent data-testid="chat-delete-message-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              {messageToDelete?.type === 'file' || messageToDelete?.type === 'voice'
                ? 'The message and its attachment will be removed for everyone in this conversation. This cannot be undone.'
                : 'The message will be removed for everyone in this conversation. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8" data-testid="chat-delete-message-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteMessage(); }}
              disabled={deletingMessage}
              className="h-8 bg-red-600 hover:bg-red-700 text-white"
              data-testid="chat-delete-message-confirm"
            >
              {deletingMessage ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
